/**
 * English sibling of `dashboard-ar.ts`.
 */

export const app = {
  name: "Operations Console",
  nameFull: "Teslm United Security Services — Operations Console",
  skipToContent: "Skip to content",
} as const;

/** Sidebar groups, in order. Flat lists stop working at nine destinations. */
export const nav = {
  groups: {
    monitoring: "Monitoring",
    operations: "Operations",
    people: "People",
    analytics: "Analytics",
    system: "System",
  },
  overview: "Overview",
  tracking: "Live tracking",
  shifts: "Shifts",
  zones: "Zones",
  leaveTypes: "Leave types",
  leave: "Leave requests",
  breaks: "Break permissions",
  deviceChanges: "Phone and device changes",
  incidents: "Security reports",
  maintenance: "Maintenance",
  updates: "Updates",
  policies: "Policies",
  attendance: "Attendance",
  employees: "Employees",
  payrollAdjustments: "Deductions and bonuses",
  payrollRuns: "Payroll runs",
  waitingList: "Waiting list",
  companies: "Companies",
  reports: "Reports",
  settings: "Settings",
  notifications: "Notifications",
  audit: "Audit log",
  collapse: "Collapse sidebar",
  expand: "Expand sidebar",
  console: "Operations console",
} as const;

/**
 * Shared vocabulary.
 *
 * Deliberately tiny, and it grows one word at a time as a screen needs it.
 * An earlier draft of this block held twenty-two entries for buttons and states
 * that did not exist yet — which is precisely the habit
 * `scripts/check-content-keys.mjs` exists to break, and it duly failed on all of
 * them. The prototype's `common` block was the same shape and shipped that way.
 */
export const common = {
  close: "Close",
  /* A boolean rendered as a word, where a checkbox would be wrong — a read-only
     cell in a table. */
  yes: "Yes",
  no: "No",
  /* Announced once per loading page, to a screen reader only. Forty grey
     rectangles are `aria-hidden`; this is what is heard instead. */
  loading: "Loading data…",
  /* "0 of 2" rather than "0 / 2". A slash between two numerals in an
     RTL paragraph is bidi-neutral, so the run reorders and the ratio renders
     visually reversed — a reader sees 2/0 where the data says 0 of 2. A word
     cannot be reordered. Shared, because coverage is stated the same way on the
     zones list and on every rota cell. */
  ofRequired: "of",
  /* Shown above forms that mix required and optional fields. The asterisk itself
     is aria-hidden on each label; this sentence is what a reader hears as the
     legend before tabbing through. */
  requiredFieldsNote: "Fields marked with * are required.",
  fieldRequired: "This field is required.",
  saveFailedRequired: "Could not save — fill in the required fields.",

  /* Colour names, not hex codes. A swatch whose accessible name is "#f78d2e"
     tells a screen-reader user nothing about what they are choosing. Shared,
     because zones and shift templates both offer a palette. */
  colors: {
    orange: "Orange",
    rust: "Rust",
    wine: "Wine",
    plum: "Plum",
    blue: "Blue",
    navy: "Navy",
    teal: "Teal",
    green: "Green",
    olive: "Olive",
  },
} as const;

/**
 * One status vocabulary for the whole console. The prototype had a different
 * pill per screen; the same state must read the same everywhere.
 *
 * @dynamic — always reached as `status[row.status]`, never by literal key, so
 * the orphan check cannot see the usages textually.
 */
export const status = {
  active: "Active",
  inactive: "Inactive",
  pending: "Under review",
  approved: "Approved",
  rejected: "Rejected",
  draft: "Draft",
  published: "Published",
  onDuty: "On duty",
  offDuty: "Off duty",
  noSignal: "No signal",
  insideZone: "Inside zone",
  outsideZone: "Outside zone",
  late: "Late",
  onTime: "On time",
  absent: "Absent",
} as const;

/**
 * @dynamic — reached as `roles[user.role]`.
 */
export const roles = {
  admin: "System administrator",
  supervisor: "Supervisor",
  guard: "Security guard",
  /* A client company's own login. Reached the same way as the other three, and
     its absence was a compile error the moment `Role` grew a fourth member —
     which is the outcome `roles[user.role]` is meant to produce. */
  company: "Client company",
} as const;

export const auth = {
  signInTitle: "Sign in",
  signInLead: "Operations console for system administrators and supervisors.",
  identifier: "Email or mobile number",
  password: "Password",
  signIn: "Sign in",
  signingIn: "Signing in…",
  signOut: "Sign out",
  forgotPassword: "Forgot password?",
  /* The reveal toggle names the action, not the state — and it flips, so the
     label is never stale. */
  showPassword: "Show password",
  hidePassword: "Hide password",

  /* The prototype had none of the following, in any form. A guard whose account
     was created could not learn their password, and nobody who forgot one could
     recover it. */
  forgotTitle: "Reset password",
  forgotLead: "Enter the national ID / residency number we have on file and we will send a verification code to your mobile.",
  forgotSubmit: "Send code",
  forgotSent: "If the email is registered with us, the link will arrive within a few minutes.",
  nationalIdLabel: "National ID / residency number",
  nationalIdHint: "10 digits starting with 1 or 2.",
  otpTitle: "Enter verification code",
  otpLead: "We sent a code to the mobile number",
  otpLabel: "Verification code",
  otpSubmit: "Verify code",
  otpResend: "Resend code",
  otpResendIn: "Resend in",
  otpVerifying: "Verifying…",
  otpSeconds: "s",
  passwordResetDone: "Password set. Sign in with your account.",
  sessionSupersededBanner: "Signed in from another device. This session was ended.",

  resetTitle: "Set a new password",
  resetLead: "Choose a new password for your account.",
  resetSubmit: "Set password",
  newPassword: "New password",
  confirmPassword: "Confirm password",
  passwordHint: "At least 8 characters.",
  emailLabel: "Email",
  sending: "Sending…",
  saving: "Saving…",
  backToSignIn: "Back to sign in",
  requestNewLink: "Request a new link",

  /* A spent or expired link is told apart from an invalid one. The token is
     unguessable, so saying which it is reveals nothing to anyone else — and
     "request a new link" is only actionable if you know that is the problem. */
  linkExpiredTitle: "Link expired",
  linkInvalidTitle: "Invalid link",
  linkExpiredLead: "Links are valid for a limited time. Request a new link and it will be sent to your email.",
  linkUsedLead: "This link has already been used. Request a new link if you still need one.",
  inviteExpiredLead: "The activation link has expired. Ask your system administrator to send a new one.",
  inviteUsedLead: "This account has already been activated. Sign in with the password you chose.",

  inviteTitle: "Activate account",
  inviteLead: "Welcome. Choose a password to activate your account.",
  inviteSubmit: "Activate account",

  resubmitTitle: "Edit application details",
  resubmitLead: "Update your details and resubmit for review. The account returns to pending approval.",
  resubmitReasonLabel: "Previous rejection reason",
  resubmitName: "Name",
  resubmitPhone: "Mobile number",
  resubmitSubmit: "Resubmit for approval",
  resubmitDoneTitle: "Application resubmitted",
  resubmitDoneLead: "Your application is now awaiting administrator approval. You will be notified when a decision is made.",
  resubmitExpiredLead: "The edit link has expired. Contact your system administrator to request a new one.",
  resubmitUsedLead: "This link has already been used. If you need to make further changes, contact your system administrator.",
  resubmittedBanner: "Your application has been resubmitted and is awaiting approval.",

  /* Errors are specific on purpose. The prototype collapsed every server error
     code — email_in_use, invalid_supervisor, missing_fields, upload_error —
     into one "Something went wrong". */
  invalidCredentials: "Email or password is incorrect.",
  linkExpired: "This link has expired. Request a new one.",
  passwordTooShort: "Password must be at least 8 characters.",
  passwordMismatch: "Passwords do not match.",
  nationalIdInvalid: "National ID must be 10 digits and start with 1 or 2.",
  nationalIdNotFound: "This national ID is not registered in the system.",
  phoneMissing: "No mobile number is linked to this account. Contact your system administrator.",
  otpInvalid: "Verification code is incorrect.",
  otpExpired: "This code has expired. Request a new one.",
  otpLocked: "Too many attempts on this code. Request a new one.",
  smsUnavailable: "Could not send the message. Try again later.",
  otpResendWait: "Wait a moment before requesting another code.",
  rateLimited: "Too many attempts. Wait a moment and try again.",
} as const;

/** Where a guard lands. The prototype dead-ended them on a bare text node. */
export const mobileOnly = {
  title: "This console is for supervisors and system administrators",
  lead: "Record attendance and submit requests from the guard mobile app on your phone.",
} as const;

export const errors = {
  forbiddenTitle: "Access denied",
  /* A silent redirect reads as a broken link. The prototype bounced a
     supervisor away from /zones with no message at all. */
  forbiddenLead: "This page is available to a different role. Contact your system administrator if you need access.",
  serverTitle: "Could not complete the operation",
  serverLead: "An unexpected error occurred. Try again, and if it persists contact support.",
  signedInAs: "You are signed in as",
  goHome: "Go to overview",
  /* Shown by the segment error boundary. Deliberately says what to do next
     rather than apologising. */
  reload: "Try again",
  /* Client-side transport fallbacks in `src/config` — used when the API
     never returns a localised sentence (network down, empty body, 500 code). */
  sessionExpired: "Session expired. Sign in again.",
  sessionSuperseded: "Signed in from another device. Sign in again to continue.",
  networkUnavailable: "Could not reach the service. Check your network and try again.",
  operationFailed: "Could not complete the operation. Try again.",
  loginFailed: "Could not sign in. Try again.",
  linkInvalid: "This link is not valid.",
  internalError:
    "An unexpected server error occurred. Try again, and if it persists contact support",
  internalErrorRequest: "with request id",
  capacityFull: "Capacity is full. Force activation, increase the limit, or deactivate another account.",
  candidatesLoadFailed: "Could not load the people list. Try again.",
} as const;

/**
 * The Overview — built last, because every tile has to link to a filtered list
 * that already exists. The prototype's `/` was a bare role redirect.
 */
export const overview = {
  greeting: "Hello,",
  lead: "Operational summary right now.",

  /* KPI tiles. Each one links somewhere. */
  onDutyNow: "On duty now",
  ofActive: "of active guards",
  coverageToday: "Today's shift coverage",
  shiftsCovered: "shifts covered",
  lateToday: "Late today",
  onTimeToday: "On time today",
  pendingLeave: "Leave requests under review",
  neverPinged: "Never reported location",
  understaffedZones: "Zones below required staffing",
  noneTile: "None",

  /* Coverage gaps: the panel that matters most. */
  gapsTitle: "Coverage gaps in the next 24 hours",
  gapsLead: "Published shifts starting soon with fewer assigned guards than required.",
  gapsEmpty: "All upcoming shifts are fully covered.",
  gapsShort: "short",
  gapsGuards: "guard",
  gapsFix: "Fix in schedule",

  /* Alerts: things that need a human. */
  alertsTitle: "Needs your attention",
  alertsEmpty: "Nothing needs your attention right now.",
  alertLeave: "Leave requests awaiting your decision",
  alertUnclosed: "Check-ins without matching check-outs",
  alertStale: "Guards with stale location signal",
  alertGeofence: "Out-of-zone punches today",
  alertUnderstaffed: "Zones below required coverage",
  alertInvites: "Accounts not yet activated by their owners",
  alertCompanyContracts: "Company contracts nearing expiry",

  /* Activity feed. */
  activityTitle: "Recent punches",
  activityEmpty: "No punches recorded today yet.",
  activityCheckIn: "checked in",
  activityCheckOut: "checked out",
  activityOutside: "outside zone",

  /* Sparkline. */
  trendTitle: "Punctuality — last 14 days",
  trendLead: "Share of guards who checked in on time each day. Days with no scheduled shifts appear blank, not zero.",
  trendUnit: "Percent",
  trendEmpty: "No shifts scheduled in this period.",
  trendLast: "Last completed day",
  trendChange: "Change over the period",
  trendAverage: "Period average",
  trendUpOne: "Up one point",
  trendUp: "Up {n} points",
  trendDownOne: "Down one point",
  trendDown: "Down {n} points",
  trendFlat: "No change",
  trendReport: "View report",
  activityAll: "Full attendance log",
  incidentOpen: "View open reports",
  incidentBannerOne: "One emergency report has not been received yet",
  incidentBannerMany: "{count} emergency reports have not been received yet",
  maintenanceUntil: "The guard app is down for maintenance until {time}",
  maintenanceManage: "Manage maintenance",

  /* Freshness. A dashboard with no timestamp cannot be told apart from a stale one. */
  asOf: "Updated as of",

} as const;

/** The Employees module. */
export const employees = {
  backToList: "Back to employees",
  title: "Employees",
  lead: "Active guards and supervisors only — pending and inactive accounts live on the Waiting list.",

  newTitle: "Add employee",
  newLead: "The employee will receive an activation link by email to set their password.",
  create: "Add employee",
  openWaitingList: "Waiting list",
  editTitle: "Edit details",
  save: "Save changes",
  cancel: "Cancel",

  /* Field labels. Every one sits above its input, never inside it as a
     placeholder — a placeholder disappears exactly when the user needs it. */
  name: "Full name",
  email: "Email",
  phone: "Mobile number",
  phoneHint: "Optional. Example: 0501234567",
  role: "Role",
  supervisor: "Assigned supervisor",
  supervisorHint: "Required for guards.",
  zonesLabel: "Zones",
  zonesHint: "A guard may be assigned to more than one zone.",
  noSupervisor: "No supervisor",
  choose: "Choose…",
  supervisorNotActivated: "Account not activated",
  noZonesYet: "No zones yet. Add a zone first from the Zones screen.",
  saving: "Saving…",

  /* The record's tabs.

     Tabs rather than five routes: they are five views of one person, and an
     operator flips between them. The active tab is a search param, so a
     particular view is linkable and the back button steps through them.

     Only the open tab fetches. Opening a record costs one query, not five. */
  tabOverview: "Details",
  tabShifts: "Shifts",
  tabAttendance: "Attendance",
  tabLeave: "Leave",
  tabAudit: "Audit log",

  historyWhen: "Date",
  /* Every capped list says it is capped. A truncated list that does not admit it
     is worse than a short one — the reader has no way to tell. */
  historyCapped: "Last 20 records. Total:",
  historyShiftsWindow: "Last 60 shifts within one year.",
  historyViewAll: "View all",
  auditBatchRow: "Bulk operation",

  shiftsEmptyTitle: "No shifts recorded",
  shiftsEmptyLead: "No shifts have been assigned to this employee in the last year. Assign shifts from the Shifts screen.",
  attendanceEmptyTitle: "No attendance records",
  attendanceEmptyLead: "This employee has not recorded any attendance yet. Attendance is recorded from the mobile app.",
  leaveEmptyTitle: "No leave requests",
  leaveEmptyLead: "This employee has not submitted any leave requests. Requests are submitted from the mobile app.",
  auditEmptyTitle: "No changes recorded",
  auditEmptyLead: "No one has modified this employee's details since the account was created.",


  /* Table */
  columnName: "Name",
  columnRole: "Role",
  columnSupervisor: "Supervisor",
  columnZones: "Zones",
  columnJobTitle: "Job title",
  columnStatus: "Status",
  columnCreated: "Date added",
  /* Sequential job number — global, assigned on create, never reused. */
  columnEmployeeNo: "Employee number",
  columnNationality: "Nationality",
  columnGovernmentSupported: "Government supported",
  exportReport: "Employee report",
  columnLastLogin: "Last sign-in",

  searchPlaceholder: "Search by name, email, mobile, or employee number",
  employeeNo: "Employee number",
  employeeNoHint: "Assigned automatically on create and cannot be changed.",
  employeeNoPending: "Assigned automatically on save",
  filterRole: "Role",
  filterStatus: "Status",
  filterZone: "Zone",
  filterSupervisor: "Supervisor",

  /* The account lifecycle, one label per `accountStatus` value plus the invite
     state that rides on top of `active`. Five statuses rather than the two the
     prototype had: a hire now waits for a decision, and a decision can land on a
     queue instead of on an account. */
  pendingInvite: "Account not activated",
  statusActive: "Active",
  statusPending: "Awaiting approval",
  statusWaitingList: "Waiting list",
  statusSuspended: "Suspended",
  statusRejected: "Rejected",
  /** FIFO rank while queued — `{n}` is the 1-based position in the company pool. */
  queuePosition: "Waiting list position",
  queuePositionValue: "Position {n}",
  waitingListTitle: "Waiting list",
  waitingListLead:
    "Everyone who is not active yet — new applicants, seat queue, on hold, and rejected. Filter by status, or add a new employee here.",
  waitingListEmptyTitle: "No one in the pipeline",
  waitingListEmptyLead:
    "Add an employee to start a hire, or approve applications when they arrive. Active staff appear on the Employees page.",
  columnWaitingSince: "Queued since",
  backToEmployees: "Back to employees",
  actionsLabel: "Actions",
  showMore: "Show more per page",
  neverSignedIn: "Never signed in",
  noZones: "No zone",
  noPhone: "—",

  /* Bulk actions.

     The prototype had no multi-select anywhere, so standing down a departing
     shift of ten guards was ten trips through a row menu.

     Every string names a count. A bulk confirmation that does not say how many
     rows it covers is asking the operator to trust a selection they made across
     three pages of scrolling. */
  selected: "selected",
  selectAll: "Select all",
  clearSelection: "Clear selection",
  bulkDeactivate: "Deactivate selected",
  bulkReactivate: "Reactivate selected",
  bulkAssignZones: "Assign zones",

  bulkDeactivateTitle: "Deactivate selected accounts",
  bulkDeactivateBody:
    "These account holders will not be able to sign in, and their current sessions will end immediately — on the web and on the mobile app. Their data and attendance history remain unchanged.",
  bulkReactivateTitle: "Reactivate selected accounts",
  bulkReactivateBody: "These account holders will be able to sign in again with the same password.",

  bulkAssignZonesTitle: "Assign zones to selected",
  /* Said in the dialog, not only in a code comment: the prototype's quick-assign
     replaced a guard's zones instead of adding to them, and a guard left on zero
     zones is allowed to check in anywhere by the geofence rule. */
  bulkAssignZonesBody:
    "Zones are added to each guard's current zones and do not replace them. To remove a zone from a guard, use the employee page or the zone page.",
  bulkAssignZonesConfirm: "Add zones",
  bulkAssignZonesPick: "Zones to add",

  bulkSendSms: "Send SMS",
  bulkSendEmail: "Send email",
  bulkSendSmsTitle: "Send SMS to selected",
  bulkSendEmailTitle: "Send email to selected",
  bulkSendSmsBody:
    "The message is sent to the mobile numbers on file for the selected accounts. Accounts without a valid mobile number are skipped.",
  bulkSendEmailBody:
    "The email is sent to the addresses on file for the selected accounts. Accounts without an email are skipped.",
  bulkSendSmsConfirm: "Send messages",
  bulkSendEmailConfirm: "Send email",
  bulkSendNotification: "Send notification",
  bulkSendNotificationTitle: "Send notification to selected",
  bulkSendNotificationBody:
    "The notification reaches the selected guards and supervisors in the app when it is open, or as a phone notification when it is closed. Admin accounts, client accounts, and your own account are skipped.",
  bulkSendNotificationConfirm: "Send notification",
  bulkMessageSubject: "Subject",
  bulkMessageTitle: "Title",
  bulkMessageBody: "Message body",
  bulkMessageBodyHintSms: "Up to 500 characters. Sent as-is to the mobile.",
  bulkMessageBodyHintEmail: "Up to 5,000 characters.",
  bulkMessageBodyHintNotification: "Up to 120 characters for the title and 1,000 for the message.",
  bulkSkippedSmsWhy: "No valid mobile number, or the account is outside your scope.",
  bulkSkippedEmailWhy: "No email, or the account is outside your scope.",
  bulkSkippedNotificationWhy: "The account is not a guard or supervisor, or it is your own.",
  bulkSelectMessageFirst: "Enter the message body first.",
  bulkSelectSubjectFirst: "Enter the subject and message body first.",
  bulkSelectNotificationFirst: "Enter the title and notification text first.",
  bulkEmailAttach: "Attach file",
  bulkEmailAttachHint:
    "Optional — PDF, image, Word, or Excel. Up to 10 MB per file, 5 files max.",
  bulkEmailAttachRemove: "Remove attachment",
  bulkEmailAttachmentTooLarge: "File exceeds 10 MB.",
  bulkEmailAttachmentTotalTooLarge: "Attachments exceed the size limit for sending.",
  bulkEmailAttachmentLimit: "You can attach up to 5 files.",
  bulkEmailAttachmentType: "File type not allowed. Allowed: PDF, image, Word, or Excel.",

  bulkCount: "Count:",
  bulkSkipped: "Will be skipped:",
  bulkSkippedWhy: "Your own account, or an account already in the target state.",
  /* The reason the row's own switch is off, said rather than left to be guessed
     from a greyed-out control. */
  cannotDeactivateSelf: "You cannot deactivate your own account.",
  bulkSkippedZonesWhy: "Zones are assigned to active guards only, within your team.",
  bulkSelectZonesFirst: "Select at least one zone.",


  /* Actions */
  view: "View",
  edit: "Edit",
  deactivate: "Deactivate account",
  reactivate: "Reactivate",
  resendInvite: "Resend activation link",

  /* Confirmations. The prototype used window.confirm for its one destructive
     action and nothing at all for the rest. */
  deactivateTitle: "Deactivate account",
  deactivateBody:
    "The employee will not be able to sign in, and their current sessions will end. Their data and attendance history remain unchanged.",
  deactivateConfirm: "Deactivate account",
  reactivateTitle: "Reactivate account",
  reactivateBody: "The employee will be able to sign in again with the same password.",
  reactivateConfirm: "Reactivate",

  /* Deciding a hire.

     The approve dialog names both possible outcomes because the operator does
     not choose between them — the seat cap does — and being told afterwards that
     somebody went on a queue reads as a failed action. */
  approve: "Approve",
  reject: "Reject",
  activateWaiting: "Activate from waiting list",
  enqueueWaiting: "Add to waiting list",
  approveTitle: "Approve account",
  approveBody:
    "If a seat is available, the account becomes active and an activation link is sent. If capacity is full, the account is placed on the waiting list.",
  /* Same seat-cap outcomes as Approve, said for a row that was previously refused. */
  approveFromRejectedBody:
    "This reverses the rejection. If a seat is available, the account becomes active and an activation link is sent. If capacity is full, the account is placed on the waiting list.",
  enqueueWaitingTitle: "Add to waiting list",
  enqueueWaitingBodyPending:
    "The account will be placed on the waiting list without activating now — even if a seat is open. It will appear on the Waiting list screen until you activate it or a seat opens automatically.",
  enqueueWaitingBodyActive:
    "The account will leave the active seat pool, current sessions will end, and it will be placed on the waiting list. Another person in the queue may be activated in their place.",
  enqueueWaitingConfirm: "Add to waiting list",
  rejectTitle: "Reject account",
  rejectBody: "The account will not be able to sign in. You must enter a rejection reason and choose a notification method.",
  rejectReason: "Rejection reason",
  rejectChannel: "Notification method",
  rejectChannelSms: "SMS only",
  rejectChannelEmail: "Email only",
  rejectChannelBoth: "SMS and email",
  activateWaitingTitle: "Activate from waiting list",
  activateWaitingBody:
    "The account will be activated immediately (even if not first in the queue). If no seat is available, you must confirm forced activation.",
  activateWaitingForce: "Force activation despite full capacity",
  /* Mirrors the API's own refusal word for word, because the dialog compares the
     two to decide whether to offer the force checkbox — the result carries a
     sentence, not a machine code. See `approval-dialogs.tsx`. */
  capacityFull: "Capacity is full. Force activation, increase the limit, or deactivate another account.",

  /* Outcomes, stated specifically. The prototype collapsed every server error
     into one "Something went wrong" and reported success by silently closing the modal. */
  updated: "Changes saved.",

  emptyTitle: "No active employees yet",
  emptyLead: "New hires are added from the Waiting list. Once activated, they appear here.",
  emptyFilteredTitle: "No matching results",
  emptyFilteredLead: "Try adjusting the search or removing some filters.",
  clearFilters: "Clear filters",

  /* The client this person works on. Read on the form, the filter and the table.
     A supervisor holds a company and a guard inherits it from their supervisor,
     which is why the guard-side copy says "chosen automatically" rather than
     offering a field that would let the two disagree. */
  company: "Client company",
  companyHint: "The company this supervisor works for.",
  companyFromSupervisor: "Set automatically from the supervisor's company.",
  noCompany: "No company",
  noCompaniesYet: "No companies yet — add a company first.",
  addCompany: "Add company",
  filterCompany: "Company",
  filterDepartment: "Department",
  filterCity: "Work city",

  resetPassword: "Send password reset link",
  resetPasswordTitle: "Send password reset link",
  resetPasswordBody:
    "The employee will receive a link by email to set a new password. Their current password remains valid until the new one is set, and any link sent earlier will stop working. When they set it, their sessions on all devices will end.",
  resetPasswordConfirm: "Send link",

  errors: {
    nameTooShort: "Name must be at least 2 characters.",
    nameTooLong: "Name is too long.",
    emailInvalid: "Email address is invalid.",
    phoneInvalid: "Mobile number is invalid. Example: 0501234567",
    roleInvalid: "Choose a valid role.",
    supervisorRequired: "Select the supervisor responsible for this guard.",
    supervisorNotAllowed: "Supervisor is set for guards only.",
    companyRequired: "Select the company this supervisor works for.",
    companyNotAllowed: "Company is set for supervisors only.",
    rejectionReasonRequired: "Enter a rejection reason.",
  },
} as const;

export const employeeRecord = {
  /* The rail. HR file first, then operational history. */
  groupProfile: "HR file",
  groupEmployment: "Employment",
  groupHistory: "History",

  sectionOverview: "Overview",
  sectionJob: "Job info",
  sectionPersonal: "Personal info",
  sectionContact: "Contact details",
  sectionEmergency: "Emergency contact",
  sectionAddress: "Address",
  sectionEducation: "Education",
  sectionAdditional: "Additional info",
  sectionDocuments: "Documents",
  sectionContract: "Contract & work",
  sectionBank: "Bank details",
  sectionInsurance: "Insurance",
  sectionLeave: "Leave",
  sectionLeaveTypes: "Leave types",
  sectionLeaveRequests: "Leave requests",
  sectionShifts: "Shifts",
  sectionAttendance: "Attendance",
  sectionAudit: "Audit",

  /* Personal information */
  employeeNo: "Employee number",
  employeeNoHint: "Assigned automatically on create and cannot be changed.",
  nationalId: "National ID",
  nationalIdHint: "10 digits, starting with 1 or 2.",
  idType: "ID type",
  idTypeNational: "National ID",
  idTypeIqama: "Iqama",
  idExpiresOn: "ID / Iqama expiry",
  nameArTitle: "Name in Arabic",
  nameEnTitle: "Name in English",
  firstName: "First name",
  secondName: "Second name",
  thirdName: "Third name",
  lastName: "Last name",
  fullNameHint: "The name shown across the console is built from these parts.",
  nationality: "Nationality",
  religion: "Religion",
  employeeType: "Employee type",
  title: "Title",
  gender: "Gender",
  maritalStatus: "Marital status",
  bloodType: "Blood type",
  birthDate: "Date of birth",
  iban: "IBAN",
  gosiNumber: "GOSI number",
  disbursementMethod: "Payment method",
  disbursementUnset: "Inferred from the IBAN",
  disbursementBank: "Bank transfer",
  disbursementCash: "Cash",
  disbursementCheque: "Cheque",
  ibanHint: "Salary account. SA followed by 22 digits.",
  bankName: "Bank name",
  accountHolderName: "Account holder name",
  accountNumber: "Account number",
  phoneAlt: "Secondary mobile",
  governmentSupported: "Government supported",
  notes: "Notes",

  /* Job */
  jobCompany: "Company / branch",
  jobStatus: "Employee status",
  jobSupervisor: "Direct manager",
  jobZones: "Sites / zones",
  jobEditAccount: "Edit account details",
  employmentStartDate: "Employment start date (first day on the job)",
  workCity: "Work city / area",

  profileStatusDraft: "Draft",
  profileStatusComplete: "Complete",
  profileSaveDraft: "Save as draft",
  profileMarkComplete: "Mark complete",
  profileDraftHint: "This HR file is still a draft — required fields can be filled over time.",
  profileCompleteHint: "Required HR fields are filled. You can reopen it as a draft if something changes.",

  /* Additional */
  iqamaOccupation: "Occupation on Iqama",
  sponsorship: "Sponsorship",
  vehicleType: "Vehicle type",
  vehiclePlate: "Plate number",
  previousExperience: "Previous experience",
  languageLevel: "Language level",

  /* Contract extras */
  workType: "Work type",
  workTypeFullTime: "Full-time",
  workTypePartTime: "Part-time",
  workTypeShifts: "Shifts",
  payMethod: "Pay calculation",
  payMethodMonthly: "Monthly",
  payMethodDaily: "Daily",
  payMethodHourly: "Hourly",
  payMethodPerCall: "Per call",
  housingAllowance: "Housing allowance",
  transportAllowance: "Transport allowance",
  totalSalary: "Total salary",

  /* Contact */
  contactPrimaryTitle: "Primary contact details",
  contactPrimaryHint: "Primary mobile and email are changed from account details — those are used to sign in.",
  contactPrimaryEdit: "Edit account details",
  contactsTitle: "Contacts",
  contactName: "Name",
  contactRelation: "Relationship",
  contactPhone: "Mobile number",
  contactEmail: "Email",
  contactIsPrimary: "Primary contact",

  /* Address */
  addressKind: "Address type",
  region: "Region",
  city: "City",
  district: "District",
  buildingNo: "Building number",
  postalCode: "Postal code",
  additionalNo: "Additional number",
  unitNo: "Unit number",
  addressIsPrimary: "Primary address",
  mapTitle: "Location on map",
  coordinates: "Coordinates",
  markerHint: "Search for a place above, or click the map to set the location and drag the marker to fine-tune it.",
  markerClear: "Remove marker",
  noMarker: "No location set.",

  /* Education */
  degree: "Degree",
  qualification: "Qualification",
  graduationDate: "Graduation year",
  college: "College",
  studyMode: "Study mode",

  /* Insurance */
  policyNo: "Policy number",
  insuranceType: "Insurance type",
  insuranceProvider: "Insurance provider",
  insuranceClass: "Insurance class",
  holderName: "Policy holder name",
  annualCost: "Annual cost",
  issueDate: "Issue date",
  expiryDate: "Expiry date",
  active: "Active",

  /* Contract */
  contractNo: "Contract number",
  contractType: "Contract type",
  jobTitle: "Job title",
  department: "Department",
  startDate: "Start date",
  endDate: "End date",
  probationEndDate: "Probation end date",
  probationInputMode: "Probation input method",
  probationByDays: "By days",
  probationByDate: "By date",
  probationDays: "Probation period (days)",
  probationDaysHint: "Counted from the contract start date.",
  probationNeedsStart: "Set the start date first.",
  probationEndsOn: "Ends on",
  probationDaysUnit: "days from start date",
  probationBeforeStart: "Date is before the contract start.",
  basicSalary: "Basic salary",
  allowances: "Allowances",
  noticePeriodDays: "Notice period (days)",
  contractStatus: "Contract status",
  renewContract: "Renew contract",
  renewContractTitle: "Renew contract",
  renewContractLead:
    "A new contract will be created with the same duration, starting on the current end date. Review and edit before confirming — the current contract becomes expired.",

  terminateContract: "Terminate contract",
  terminateContractTitle: "Terminate contract",
  terminateContractLead:
    "The contract becomes terminated, the employee account is set inactive, and a notification email is sent. Available in the last 60 days before the end date.",
  terminateContractConfirm: "Confirm termination",
  terminationReason: "Termination reason",
  terminationReasonPlaceholder: "Choose…",
  terminationReasonRoleNoLongerNeeded: "Role no longer needed",
  terminationReasonPoorPerformance: "Poor performance",
  terminationReasonEmployeeResignation: "Employee resignation",
  terminationReasonRestructuring: "Restructuring",
  terminationReasonOther: "Other",
  terminationReasonOtherLabel: "Specify other reason",
  terminationDetails: "Additional explanation / details",
  terminationDetailsHint: "Internal notes for the termination record.",
  terminationShareDetails: "Include the explanation in the employee email",
  terminationAttachment: "Attach document (optional)",
  terminationAttachmentPick: "Choose file",
  terminationAttachmentUploading: "Uploading…",
  terminationAttachmentRemove: "Remove attachment",
  terminationAttachmentHint: "PDF, Word, Excel, image, or text — up to 10 MB.",
  terminationAttachmentTooLarge: "Attachment is too large (max 10 MB).",
  terminationAttachmentType: "This file type is not allowed for attachments.",

  /* Documents */
  documentKind: "Document type",
  documentNumber: "Document number",
  issuePlace: "Issuing authority",
  attachment: "Attachment",
  attachmentOpen: "Open attachment",
  attachmentNone: "No attachment",
  documentsRestricted: "Documents are available to system administrators only.",

  /* The attachment control inside the document form. The file goes straight
     to private storage via a short-lived signed URL; the form holds only the key. */
  attachmentField: "Document file",
  attachmentHint: "JPG, PNG, WebP, or PDF, up to 8 MB.",
  attachmentUploading: "Uploading…",
  attachmentUploaded: "Uploaded — saved with the document when you save.",
  attachmentReplace: "Replace file",
  attachmentChoose: "Choose file",
  attachmentClear: "Remove attachment",
  attachmentCurrent: "File saved on this document.",
  attachmentTooLarge: "File exceeds 8 MB.",
  attachmentPutFailed: "Could not upload the file to storage. Try again.",

  /* Leave types */
  leaveTypesTitle: "Employee leave configuration",
  leaveTypesHint: "Only selected types can be used when this employee files a leave request.",
  entitlementDays: "Day count",
  entitlementPlaceholder: "Default",
  entitlementHint: "Leave blank to use the type's default day count.",
  payPaid: "Paid",
  payPartial: "Partially paid",
  payUnpaid: "Unpaid",
  grantedCount: "types selected",

  ledgerTitle: "Leave ledger",
  ledgerHint:
    "Monthly accrual, carry (plus final-quarter credit), and adjustments for this service year from the hire anniversary. Applies to every leave type with an annual cap. Remaining is rounded down.",
  ledgerKind: "Entry",
  ledgerDays: "Days",
  ledgerPeriod: "Period",
  ledgerEmpty:
    "No ledger entries yet — entitlement accrues monthly from the type cap; unused balance carries on the hire anniversary.",
  ledgerErrorTitle: "Could not load the leave ledger",
  ledgerErrorLead:
    "Leave-type configuration above is fine; only the balance ledger (accrual and carry) failed. Try refreshing the page.",
  ledgerKindOpening: "Opening",
  ledgerKindAccrual: "Accrual",
  ledgerKindCarry: "Carry",
  ledgerKindAdjustment: "Adjustment",

  /* Shared table + form chrome */
  add: "Add",
  edit: "Edit",
  remove: "Remove",
  save: "Save",
  cancel: "Cancel",
  saving: "Saving…",
  saved: "Changes saved.",
  updatedBy: "Last updated by",
  actions: "Actions",
  choose: "Choose…",
  none: "—",
  removeTitle: "Delete record",
  removeBody: "This will be permanently removed from the employee file. This action cannot be undone.",
  removeConfirm: "Delete",

  capped: "Showing part of the records. Total:",

  emptyTitle: "No data",
  emptyLead: "Nothing has been recorded in this section yet.",

  tabData: "Data",
  tabWork: "Work & contracts",
  tabQualifications: "Qualifications & experience",
  tabAttendanceLog: "Attendance log",
  tabPerformance: "Performance",
  tabPayroll: "Payroll",
  tabLog: "Log",
  tabLogShifts: "Shifts",
  tabLogChanges: "Changes",

  photoLabel: "Personal photo",
  photoHint: "JPG, PNG, or WebP, up to 8 MB. The photo is cropped square and shown on the profile and the lists.",
  photoReplace: "Replace",
  photoRemove: "Remove",
  photoChoose: "Choose a photo",
  photoTypeInvalid: "Only JPG, PNG, or WebP is accepted.",
  photoSaved: "Personal photo updated.",
  photoSaveFailed: "The personal photo could not be saved.",

  dataEmail: "Email",
  dataPhone: "Mobile",
  dataZones: "Zones",
  dataNoZone: "No zone",
  dataJoined: "Added",
  dataLastLogin: "Last sign-in",
  dataStatus: "Status",
  dataNever: "Has not signed in",

  commitmentTitle: "Commitment",
  commitmentUp: "Up {n} points from last month",
  commitmentDown: "Down {n} points from last month",
  commitmentFlat: "Unchanged from last month",
  weightPunctuality: "Punctuality",
  weightHours: "Counted hours of scheduled",
  weightClean: "Work days without a violation",
  weightShare: "{n}%",
  howCalculated: "How it's calculated",
  howCalculatedBody:
    "40% shift-start punctuality · 40% counted hours of scheduled hours · 20% entitled days without a violation. Only entitled shifts count: attended days and absences.",
  kpiCountedHours: "Counted hours",
  kpiOfScheduled: "of {n} scheduled",
  kpiLate: "Late occurrences",
  kpiNone: "None",
  kpiPrevious: "{n} last month",
  kpiOvertime: "Approved overtime",
  kpiOvertimePending: "{n} awaiting approval",
  kpiAbsences: "Absences",
  kpiViolations: "Violations",
  kpiOutOfZone: "Out of zone",
  kpiBreak: "Exceeded break: {n}",
  kpiBreakNone: "Exceeded break: —",
  weeklyCommitment: "Weekly commitment — 12 weeks",
  weeklyCommitmentLead:
    "The percentage for each Saturday-to-Friday week. The dashed line is the supervisor's team average for the selected month.",
  weeklyHours: "Weekly hours",
  hoursScheduled: "Scheduled",
  hoursCounted: "Counted",
  hoursOvertime: "Approved overtime",
  hoursAxis: "Axis in hours",
  latenessByWeekday: "Lateness by weekday",
  latenessEmpty: "No lateness this month.",
  monthDays: "Days of the month",
  legendPresent: "Present",
  legendIssue: "Present with lateness or a violation",
  legendAbsent: "Absent",
  legendLeave: "Leave",
  legendRest: "Rest day or holiday",
  legendOvertime: "Approved overtime",
  violationsTitle: "Violations in the month",
  violationsEmpty: "No violations this month.",
  openDay: "Open day",
  violationLate: "{n} late",
  violationZone: "{n} out of zone",
  teamAverage: "Team average",
  attendanceDayLead: "Punches for this day.",

  payrollTitle: "Salary statements",
  payrollMonth: "Month",
  payrollGross: "Gross",
  payrollDeductions: "Deductions",
  payrollNet: "Net",
  payrollStatus: "Status",
  payrollEstimated: "Estimated",
  payrollStatusApproved: "Approved",
  payrollPaid: "Paid",
  payrollOpenLog: "Attendance log",
  payrollOpenStatement: "Open statement",
  payrollFootnote:
    "Estimated means the month's run is not approved yet, so the amount still changes. Approved is locked. Paid has a recorded payment.",
  payrollItemsTitle: "Deductions and bonuses",
  payrollItemsEmpty: "No items for this employee.",
  payrollViewAll: "View all",
  payrollAdd: "Add deduction or bonus",
  payrollKind: "Type",
  payrollKindDeduction: "Deduction",
  payrollKindBonus: "Bonus",
  payrollAmount: "Amount",
  payrollReason: "Reason",
  payrollAddSubmit: "Add",
  payrollAdded: "Item added.",
  payrollPaidMonth: "This month is approved and no longer accepts new items.",
  statementBack: "Back to statements",
  statementEarnings: "Earnings",
  statementDeductionItems: "Deduction items",
  statementBonuses: "Bonuses",
  earningBasic: "Basic salary",
  earningHousing: "Housing allowance",
  earningTransport: "Transport allowance",
  earningAllowances: "Other allowances",
  earningOvertime: "Overtime",
  statementPaidOn: "Paid on",
  statementEmptyLine: "No items.",
  statementNet: "Net",
  payrollApprove: "Approve",
  payrollApproved: "Statement approved.",
  payrollApproveFailed: "The statement could not be approved.",
  dataDevice: "Bound device",
  qualificationsCertificates: "Qualifications and certificates",
  qualificationsCertificatesLead: "Academic and professional certificates.",
  certSpecialty: "Specialty",
  certIssuer: "Issuing body",
  certDate: "Graduation date",
  experienceTitle: "Previous experience",
  experienceLead: "Earlier employers and how long the employee worked there.",
  experienceEmployer: "Employer",
  experienceFrom: "From",
  experienceTo: "To",
  languagesTitle: "Languages",
  languagesLead: "Languages the employee speaks, and the level.",
  languageName: "Language",
  languageNotes: "Notes",
  languageBeginner: "Beginner",
  languageIntermediate: "Intermediate",
  languageAdvanced: "Advanced",
  languageNative: "Native",
  dutyScheduled: "Scheduled",
  dutyCounted: "Counted",
  dutyLateAfter: "Late after allowance",
  dutyApprovedOt: "Approved overtime",
  dutyPendingOt: "Pending overtime",
  dutyAbsences: "Absences",
  dutyCommitment: "Commitment",
  dutyViolations: "Violations",
  dutyDay: "Day",
  dutyShift: "Shift",
  dutyIn: "Check-in",
  dutyOut: "Check-out",
  dutyLate: "Lateness",
  dutyExtra: "Overtime",
  dutyNotes: "Notes",
  dutyRest: "Weekly rest",
  dutyLeaveNote: "Leave",
  dutyAbsentNote: "Absent",
  dutyPendingNote: "{n} pending",
  adjustmentsTitle: "Deductions and bonuses",
  adjustmentsLead: "Deduction and bonus items for every employee.",
  adjustmentsEmpty: "No items.",
  adjustmentsEmployee: "Employee",
  logAttendance: "Attendance",
} as const;

/** @dynamic — reached as `genders[profile.gender]`. */
export const genders = {
  male: "Male",
  female: "Female",
} as const;

/** @dynamic — reached as `maritalStatuses[profile.maritalStatus]`. */
export const maritalStatuses = {
  single: "Single",
  married: "Married",
  divorced: "Divorced",
  widowed: "Widowed",
} as const;

/**
 * @dynamic — reached as `bloodTypes[profile.bloodType]`.
 *
 * The eight groups, written the way a lab report writes them. Not translated:
 * "A+" is the international notation and an Arabic rendering of it would be a
 * label nobody would recognise on a card.
 */
export const bloodTypes = {
  "A+": "A+",
  "A-": "A-",
  "B+": "B+",
  "B-": "B-",
  "AB+": "AB+",
  "AB-": "AB-",
  "O+": "O+",
  "O-": "O-",
} as const;

/** @dynamic — reached as `studyModes[row.studyMode]`. */
export const studyModes = {
  full_time: "Full-time",
  part_time: "Part-time",
} as const;

/** @dynamic — reached as `addressKinds[row.kind]`. */
export const addressKinds = {
  national: "National address",
  work: "Work address",
  other: "Other address",
} as const;

/** @dynamic — reached as `contractStatuses[row.status]`. */
export const contractStatuses = {
  draft: "Draft",
  active: "Active",
  expired: "Expired",
  terminated: "Terminated",
} as const;

export const zones = {
  backToZones: "Back to zones",
  title: "Zones",
  lead: "Boundaries of guarded sites, geofence policy, and required guard count for each.",

  newTitle: "Add zone",
  newLead: "Draw the boundary on the plan or paste coordinates. Either method works on its own.",
  create: "Add zone",
  editTitle: "Edit zone",
  save: "Save changes",
  cancel: "Cancel",
  saving: "Saving…",

  /* Fields */
  name: "Zone name",
  nameHint: "Shown in tables, reports, and on the guard app.",
  color: "Color",
  colorHint: "Distinguishes the zone on the map and in tables.",
  policy: "Geofence policy",
  policyBlock: "Block punches outside the boundary",
  policyFlag: "Accept punch with alert",
  policyBlockHint: "The guard can only check in inside the boundary.",
  policyFlagHint:
    "The punch is accepted and recorded as outside the boundary, and the supervisor is alerted. Suitable when the site boundary is approximate.",
  company: "Client company",
  noCompany: "No company",
  requiredGuards: "Guards required per shift",
  requiredGuardsHint: "Used to measure coverage gaps in the rota.",
  notes: "Notes",
  notesHint: "Optional. On-site contact number, or anything else the supervisor needs.",
  gate: "Gate",
  gateHint: "Optional. The gate this post covers at the site.",

  /* Geometry */
  boundary: "Zone boundary",
  boundaryHint:
    "Click the plan to add a point, and drag a point to move it. Or paste coordinates in the field beside it.",
  coordinates: "Coordinates",
  coordinatesHint: "One line per point: latitude then longitude. GeoJSON can be pasted as well.",
  coordinatesEmpty: "No points yet. Click the plan or paste coordinates.",
  /* The address pin. Focusable, because arrow keys are the only way to place a
     point without a pointer. */
  markerLabel: "Location marker — use arrow keys to move it",
  planTitle: "Boundary plan",
  planNote:
    "A scaled engineering plan with no basemap. When a map provider is connected, the boundary will appear over site imagery.",
  /* The satellite provider, when it is on. */
  mapLoading: "Loading map…",
  mapUnavailable: "Could not load the map. Check the Maps key and that maps.googleapis.com is allowed in CSP.",
  mapKeyMissing: "Map is shown as an engineering plan without satellite imagery — the Maps key is not set in this environment.",
  providerSubstituted:
    "The requested map provider is not enabled in this build; the engineering plan is shown instead.",
  mapSearchLabel: "Search for a place on the map",
  mapSearchPlaceholder: "Search for a city, district, or landmark…",
  undoPoint: "Remove last point",
  clearBoundary: "Clear boundary",
  vertexCount: "Point count",
  area: "Estimated area",
  centre: "Zone centre",
  detectedGeojson: "Coordinates read as GeoJSON; axis order was swapped automatically.",
  detectedLines: "Coordinates read line by line.",

  /* Table */
  columnName: "Zone",
  columnPolicy: "Geofence policy",
  columnGuards: "Guards",
  columnRequired: "Required",
  columnArea: "Area",
  columnCreated: "Date added",
  searchPlaceholder: "Search by zone name",
  filterPolicy: "Geofence policy",
  filterState: "Status",
  stateLive: "Active zones",
  stateArchived: "Archived",
  archivedBadge: "Archived",
  actionsLabel: "Actions",
  understaffed: "Understaffed",

  /* Members */
  membersTitle: "Assigned guards",
  membersLead: "Assignment is additive: adding a guard here does not remove their assignments in other zones.",
  membersEmpty: "No guards assigned to this zone.",
  assign: "Assign guards",
  assignHint: "Choose from active guards not yet assigned to this zone.",
  assignSubmit: "Assign selected",
  noAssignable: "No guards available to assign. All active guards are already assigned here.",
  remove: "Remove assignment",
  removeTitle: "Remove guard assignment",
  removeBody: "Only this zone assignment will be removed. Other assignments and attendance history remain unchanged.",
  removeBodyLastZone:
    "This is the guard's only assigned zone. After removal they will be allowed to check in from anywhere, because the geofence rule treats a guard with no zones as unrestricted.",
  removeConfirm: "Remove assignment",
  memberSince: "Assigned since",
  onlyZone: "Only zone",
  memberInactive: "Account suspended",

  /* Actions */
  view: "View",
  edit: "Edit",
  archive: "Archive zone",
  restore: "Reactivate",
  archiveTitle: "Archive zone",
  /* The consequence, stated. The prototype deleted zones behind a bare
     window.confirm and said nothing about the cascade. */
  archiveBody:
    "The zone will no longer appear in tables or new assignments. Guard assignments and attendance history are retained, and the zone can be reactivated.",
  archiveImpactNone: "No guards are currently assigned.",
  archiveImpactMembers: "Assigned guards:",
  archiveImpactUnfenced:
    "Of these will have no active zone and will be allowed to check in from anywhere:",
  archiveConfirm: "Archive zone",
  restoreTitle: "Reactivate zone",
  restoreBody: "The zone returns to tables and shift assignments with its previous assignments.",
  restoreConfirm: "Reactivate",

  /* Outcomes */
  updated: "Changes saved.",
  archived: "Zone archived.",
  assigned: "Guards assigned.",
  unassigned: "Assignment removed.",

  emptyTitle: "No zones yet",
  emptyLead: "Add the first zone, then assign guards and build the shift rota.",
  emptyArchivedTitle: "No archived zones",
  emptyArchivedLead: "Archived zones appear here and can be reactivated.",
  emptyFilteredTitle: "No matching results",
  emptyFilteredLead: "Try adjusting the search or removing filters.",
  clearFilters: "Clear filters",

  /* A supervisor is not read-only here any more: they may post their own
     guards. What they cannot do is define a zone. The note says which is which,
     rather than leaving a missing button to be interpreted. */
  readOnlyNote:
    "You can assign your team's guards to zones. Defining zones, boundaries, and geofence policy is limited to system administrators.",

  errors: {
    nameTooShort: "Zone name must be at least 2 characters.",
    nameTooLong: "Zone name is too long.",
    gateTooLong: "Gate name is too long.",
    colorInvalid: "Color must be in #RRGGBB format.",
    requiredGuardsInvalid: "Guard count must be between 1 and 99.",
    notesTooLong: "Notes are too long.",

    /* One message per geometric problem `validatePolygon` can report. The
       prototype accepted any array of three or more points, so a self-crossing
       or lat/lng-swapped ring saved cleanly and the geofence then behaved
       unpredictably against it — with no message to explain anything. */
    boundaryEmpty: "Draw the boundary or paste coordinates.",
    boundaryUnparseable: "Could not read coordinates. Use one line per point: latitude, longitude.",
    boundaryOddCount: "Incomplete number count — the last point is missing a value.",
    tooFewPoints: "The boundary needs at least three points.",
    outOfRange: "A coordinate value is outside the allowed range.",
    looksSwapped:
      "Latitude and longitude appear swapped: with this order the boundary falls entirely outside the company's operating area.",
    zeroArea: "Boundary has no area — points are collinear or identical.",
    selfIntersecting: "The zone boundary intersects itself.",
    spansAntimeridian: "The boundary wraps around the globe. Review the coordinates.",
    tooLarge: "The boundary is too large to be a guarded site. Review the coordinates.",

    noGuardsSelected: "Select at least one guard.",
  },
} as const;

/**
 * The Shifts module — the rota.
 *
 * The prototype had no schedule: one static `HH:mm` pair per guard, forever. So
 * this vocabulary is entirely new, and it is the vocabulary Reports later
 * measures against.
 */
export const shifts = {
  title: "Shifts",
  lead: "Weekly shift rota for each zone, with required and actual coverage.",

  /* The rota grid */
  weekOf: "Week of",
  previousWeek: "Previous week",
  nextWeek: "Next week",
  thisWeek: "This week",
  filterZone: "Zone",
  gridZoneColumn: "Zone",
  addShift: "Add shift",
  addShiftTo: "Add shift on",
  emptyDay: "No shifts",
  noAssignments: "No guards assigned to this shift yet.",
  shiftNotes: "Shift notes",

  /* The week's state, in one line above the grid. */
  summaryShifts: "Shifts",
  summaryAssigned: "Assigned",
  summaryRequired: "Required",
  summaryGaps: "Coverage gaps",
  summaryDrafts: "Drafts",
  allPublished: "The week is fully published.",
  noShiftsThisWeek: "No shifts in this week yet.",
  noZones: "No zones. Add a zone first — a shift is built on a zone.",
  noZonesScoped: "No guards on your team are assigned to any zone yet.",

  /* Publishing */
  publish: "Publish week",
  publishTitle: "Publish week rota",
  publishBody:
    "The rota becomes visible to guards on the app, and punctuality and coverage are measured against it. Assignments can still be changed after publishing.",
  publishCount: "Draft shifts to be published:",
  publishGapWarning: "Shifts that will publish understaffed:",
  publishConfirm: "Publish",
  published: "Rota published.",
  draftBadge: "Draft",
  publishedBadge: "Published",

  /* Copying a week forward */
  copyPrevious: "Copy previous week",
  copyTitle: "Copy previous week",
  copyBody:
    "Shifts and assignments from the previous week will be copied into this week as drafts. Existing shifts will not be duplicated.",
  copyConfirm: "Copy",
  copied: "Previous week's shifts copied.",
  /* A shift cell */
  coverage: "Coverage",
  understaffed: "Understaffed",
  overnight: "Crosses midnight",
  removeShift: "Delete shift",
  removeShiftTitle: "Delete shift",
  removeShiftBody: "The shift and its assignments will be deleted. Recorded attendance is not affected.",
  removeShiftBodyPublished:
    "This shift is published and assigned guards see it on the app. It will disappear for them.",
  removeShiftConfirm: "Delete shift",
  shiftRemoved: "Shift deleted.",
  shiftCreated: "Shift added.",

  /* Assignment */
  assignGuard: "Assign guard",
  assignHint: "Only guards assigned to this zone — the zone is the shift location.",
  assignSubmit: "Assign",
  assigned: "Assigned.",
  unassign: "Remove assignment",
  noCandidates: "No guards assigned to this zone. Assign them from the Zones screen first.",
  conflictShiftLabel: "Another shift at the same time:",
  conflictLeaveLabel: "Approved leave at this time",
  markAbsent: "Mark absent",
  absentBadge: "Absent",
  coveredBadge: "Covered",

  /* Templates */
  templatesTitle: "Shift templates",
  templatesLead: "A shift is built from a template — day, night, or any pattern you define. The template sets time and break.",
  templatesLink: "Shift templates",
  backToRota: "Back to rota",
  newTemplate: "Add template",
  editTemplate: "Edit template",
  templateName: "Template name",
  templateNameHint: "Example: Day 8–16, Night 20–4.",
  startTime: "Start time",
  endTime: "End time",
  timeHint: "Riyadh time, 24-hour format.",
  crossesMidnight: "Crosses midnight",
  crossesMidnightHint:
    "Check for night shifts such as 20:00 to 04:00. Duration is calculated from this flag, not inferred from the numbers.",
  breakMinutes: "Break (minutes)",
  breakHint: "Deducted from counted shift duration.",
  templateColor: "Color",
  templateColorHint: "Distinguishes the template in the rota.",
  duration: "Counted duration",
  span: "Span",
  columnUsage: "Usage",
  usageUpcoming: "Upcoming",
  usageNone: "Unused",
  saveTemplate: "Save template",
  createTemplate: "Add template",
  templateUpdated: "Template saved.",
  archiveTemplate: "Archive template",
  archiveTemplateTitle: "Archive template",
  archiveTemplateBody:
    "The template will no longer appear when adding a new shift. Shifts already built from it keep their times.",
  archiveTemplateUpcoming: "Upcoming shifts built from this template:",
  archiveTemplateConfirm: "Archive",
  templateArchived: "Template archived.",
  restoreTemplate: "Reactivate",
  restoreTemplateTitle: "Reactivate template",
  restoreTemplateBody: "The template returns to the list when adding a shift.",
  restoreTemplateConfirm: "Reactivate",
  templatesEmptyTitle: "No templates yet",
  templatesEmptyLead: "Add at least one template — a shift is built from it.",
  showArchivedTemplates: "Show archived",
  hideArchivedTemplates: "Hide archived",
  archivedBadge: "Archived",

  readOnlyNote: "You have view-only access to the rota.",

  errors: {
    nameTooShort: "Template name must be at least 2 characters.",
    nameTooLong: "Template name is too long.",
    timeInvalid: "Time must be in 24-hour format, e.g. 08:00.",
    /* The zero-length shift the prototype could not distinguish from a 24-hour
       one, because it inferred midnight-crossing instead of storing it. */
    zeroLength:
      "Shift start and end are identical. Check «Crosses midnight» if this is a 24-hour shift.",
    endBeforeStart:
      "End time is before start time. Check «Crosses midnight» if this is a night shift.",
    breakInvalid: "Break must be between 0 and 480 minutes.",
    breakTooLong: "Break is longer than the shift duration.",
    colorInvalid: "Color must be in #RRGGBB format.",
    requiredGuardsInvalid: "Guard count must be between 1 and 99.",
    dateInvalid: "Date is invalid.",
    /* Double-booking: the classic rota defect, invisible until the day. */
    notesTooLong: "Notes are too long.",
    generic: "Could not complete the operation. Try again.",
  },
} as const;

export const leaveGroups = {
  annual: "Annual",
  sick: "Sick",
  family: "Family",
  religious: "Religious",
  study: "Study",
  other: "Other",
} as const;

export const leave = {
  title: "Leave requests",

  /* Filing on somebody's behalf — the console's first write *into* the queue
     rather than onto it, and the only way an admin's or a supervisor's own
     leave gets recorded at all. */
  fileTitle: "File leave",
  fileIntro: "File leave on the employee's behalf. The decision stays with the supervisor, as usual.",
  fileEmployee: "Employee",
  fileSearchPlaceholder: "Choose or search by name or employee number…",
  fileNoMatches: "No matching results",
  fileSearchMore: "More people match than are shown. Keep typing to narrow the search.",
  fileRemainingTitle: "Remaining leave",
  fileOf: "of",
  fileSubstitute: "Substitute during absence",
  fileSubstituteHint: "Optional — another employee covering during this leave.",
  fileOptional: "optional",
  fileType: "Leave type",
  fileStart: "From date",
  fileEnd: "To date",
  /* Inclusive calendar days — same rule as `countLeaveDays` on the API
     (10 Sept → 15 Sept = 6 days). Annual types may still skip official
     holidays when the balance is charged; this figure is the span the
     operator picked, so they can check it before submit. */
  fileDuration: "Total days",
  fileNotes: "Notes",
  fileSubmit: "Submit request",
  fileOpen: "File leave",
  fileNoTypes: "This employee has no leave types available. Configure them on the employee record under Leave types.",
  fileAvailableBalance: "Available of this type",
  fileAvailableBalanceUnit: "days",
  fileAvailableBalanceLoading: "Loading balance…",
  fileUnlimitedType: "This type is not limited by a number of days.",
  fileOverBalance: "The selected span is longer than the balance left, and can only be filed as a leave advance.",
  fileAdvance: "Leave advance with approval",
  fileAdvanceHint: "Allows exceeding the available balance. Recorded under your name.",
  fileAvailableBalanceMissing: "No balance is recorded for this leave type on this employee.",
  fileAnnualEntitlement: "Annual entitlement",
  fileMissingEmploymentStart: "Employment start date is missing, balance can't be calculated.",
  filePendingNote:
    "The request will be filed under your name and remain under review — someone must approve it like any other request.",
  lead: "Guard leave requests and the decisions made on them, with reasons.",

  /* Queue */
  tabPending: "Under review",
  tabApproved: "Approved",
  tabRejected: "Rejected",
  tabAll: "All",
  searchPlaceholder: "Search by guard name or notes",
  filterGroup: "Category",
  filterLeaveType: "Type",
  filterGuard: "Guard",
  filterFrom: "From date",
  filterTo: "To date",
  dateRangeHint: "Includes every request that overlaps the period, not only those fully contained in it.",
  /* A native date input renders in the *browser's* locale, so it can show
     "mm/dd/yyyy" inside an Arabic console. The applied range is echoed back in
     the console's own format so the operator can check what is actually
     filtered, whatever the input looks like. */
  dateRangeApplied: "Applied period:",

  columnGuard: "Guard",
  columnType: "Type",
  columnPeriod: "Period",
  columnFiled: "Date filed",
  columnStatus: "Status",
  attachment: "Attachment",
  hasAttachment: "Has attachment",
  noAttachment: "No attachment",
  fromNow: "From now",

  /* Detail */
  requestTitle: "Leave request",
  backToQueue: "Back to list",
  period: "Period",
  duration: "Duration",
  notes: "Guard notes",
  noNotes: "No notes.",
  supervisor: "Responsible supervisor",
  guard: "Guard",

  /* The timeline the prototype serialised and never displayed. */
  timelineTitle: "Request history",
  timelineFiled: "Request submitted",
  /* Shown instead of `timelineFiled` when somebody filed on the employee's
     behalf, so a leave day recorded against a guard never looks like one they
     asked for themselves. */
  timelineFiledOnBehalf: "Request filed on behalf of employee",
  timelineDecided: "Decision issued",
  timelineReopened: "Decision reopened",
  timelineBy: "By",

  /* Leave already taken, as context for the decision — per type, since a
     category total spans allowances that have nothing to do with each other. */
  balanceTitle: "Leave balance",
  balanceType: "Type",
  balanceRemaining: "Remaining",
  balanceRequests: "Request count",
  balanceNoLimit: "No limit",
  balanceRevoked: "No longer available",
  balanceOverdrawn: "Over entitlement",
  balanceEmpty: "No leave types have been assigned to this employee yet.",
  balanceHint: "Approved only. Requests under review are not yet final.",

  /* Decision */
  decide: "Decision",
  approve: "Approve",
  reject: "Reject",
  approveTitle: "Approve request",
  /* Approve is confirmed like reject. In the prototype it was one irreversible
     click while reject demanded a typed reason. */
  approveBody: "The request will be approved and the guard will be notified. The decision can be reopened later.",
  approveConfirm: "Approve",
  approveWithAttachmentTitle: "Approve with attachment",
  approveWithAttachmentBody:
    "This leave type requires documentation before approval. Upload the proof the decision is based on, then confirm.",
  approveAttachmentLabel: "Attachment",
  approveAttachmentHint: "PDF or image, up to 8 MB.",
  approveAttachmentReady: "Ready to attach",
  approveAttachmentUploading: "Uploading…",
  rejectTitle: "Reject request",
  rejectReasonLabel: "Rejection reason",
  rejectReasonHint: "Shown to the guard. State the reason clearly.",
  rejectConfirm: "Reject request",
  reopen: "Reopen decision",
  reopenTitle: "Reopen decision",
  reopenBody: "The request returns to the review queue, and who reopened it and when is recorded.",
  reopenConfirm: "Reopen",

  /* Bulk */
  selectAll: "Select all",
  selected: "selected",
  clearSelection: "Clear selection",
  bulkApprove: "Approve selected",
  bulkApproveTitle: "Approve selected requests",
  bulkApproveBody: "Every selected request still under review will be approved, and each guard will be notified.",
  bulkApproveCount: "Requests to be approved:",
  bulkSkipped: "Selected requests skipped because they are not under review:",
  bulkSkippedAttachment:
    "Skipped — these need an attachment; open each request to upload proof and approve:",
  bulkNeedsAttachmentOnly:
    "None of the selected requests can be bulk-approved — each requires an attachment. Open them individually.",
  bulkApproveConfirm: "Approve",

  /* Outcomes */
  approved: "Request approved.",
  reopened: "Decision reopened.",
  bulkApproved: "Requests approved.",

  emptyTitle: "No requests",
  emptyLead: "Guard leave requests appear here when submitted from the app.",
  emptyFilteredTitle: "No matching results",
  emptyFilteredLead: "Try adjusting the period or removing filters.",
  clearFilters: "Clear filters",
  readOnlyNote: "You have view-only access to requests.",

  errors: {
    /* The status guard the prototype's decision handler did not have: an
       approved request could be re-decided by curl, overwriting the decision. */
    reasonRequired: "Enter a rejection reason.",
    reasonTooLong: "Reason is too long.",
    noneSelected: "No request selected.",
  },
} as const;

/** Momentary on-shift permissions — separate from day-based leave. */
export const breaks = {
  title: "Break permissions",
  lead: "Break requests for a fixed duration of 15, 30, or 60 minutes. The timer starts only after a supervisor approves.",

  approved: "Break permission approved.",
  rejected: "Break permission rejected.",
  reopened: "Request reopened.",

  tabPending: "Under review",
  tabApproved: "Approved",
  tabRejected: "Rejected",
  tabAll: "All",

  filterType: "Type",
  filterGuard: "Guard",

  columnGuard: "Guard",
  columnType: "Type",
  columnMinutes: "Duration (minutes)",
  columnWindow: "Window",
  columnFiled: "Filed",
  columnStatus: "Status",

  countsAsWorked: "Time counts as worked",
  doesNotCountAsWorked: "Time does not count as worked",
  fromNow: "From now",
  startsOnApproval: "Starts when a supervisor approves",
  notStarted: "Not started",

  requestTitle: "Break permission",
  backToQueue: "Back to queue",
  notes: "Guard notes",
  noNotes: "No notes.",
  supervisor: "Responsible supervisor",
  guard: "Guard",

  timelineTitle: "Request history",
  timelineFiled: "Request filed",
  timelineDecided: "Decision issued",
  timelineReopened: "Decision reopened",
  timelineBy: "by",

  approve: "Approve",
  reject: "Reject",
  approveTitle: "Approve request",
  approveBody: "The break starts now for the approved duration (15, 30, or 60 minutes), and the guard is notified.",
  approveConfirm: "Approve",
  rejectTitle: "Reject request",
  rejectReasonLabel: "Rejection reason",
  rejectReasonHint: "Shown to the guard. State the reason clearly.",
  rejectConfirm: "Reject request",
  reopen: "Reopen decision",
  reopenTitle: "Reopen decision",
  reopenBody: "The request returns to the review queue, and who reopened it and when is recorded.",
  reopenConfirm: "Reopen",

  emptyTitle: "No break permissions",
  emptyLead: "Break requests appear here when submitted from the app.",
  emptyFilteredTitle: "No matching results",
  emptyFilteredLead: "Try removing filters.",
  clearFilters: "Clear filters",
  readOnlyNote: "You have view-only access to requests.",

  errors: {
    reasonRequired: "Enter a rejection reason.",
    reasonTooLong: "Reason is too long.",
  },
} as const;

export const leaveTypes = {
  title: "Leave types",
  lead: "Catalogue of leave types — names, default days, and active status.",
  newTitle: "Add leave type",
  newLead: "Define the type code, names, and entitlement rules. Active types can be granted on employee records.",
  editTitle: "Edit leave type",
  create: "Add type",
  createCta: "Add type",
  save: "Save changes",
  saving: "Saving…",
  cancel: "Cancel",
  edit: "Edit",

  nameAr: "Name in Arabic",
  nameEn: "Name in English",
  nameEnHint: "Optional.",
  code: "Code",
  codeHint: "Lowercase letters, digits, and underscores. Cannot be changed after creation.",
  codeImmutable: "The code is fixed after creation so the app and integrations can rely on it.",
  group: "Category",
  pay: "Pay type",
  payPaid: "Paid",
  payPartial: "Partially paid",
  payUnpaid: "Unpaid",
  payPercent: "Pay percentage (%)",
  payPercentHint: "Required when pay type is partial (1–99).",
  defaultDays: "Default days",
  maxPerYear: "Maximum per year",
  color: "Color",
  colorHint: "Optional. #RRGGBB format.",
  sortOrder: "Display order",
  requiresAttachment: "Requires attachment on approval",
  requiresAttachmentHint:
    "When enabled, the supervisor must upload a document (e.g. sick note) before the request can be approved. Guards do not upload at filing time.",
  active: "Active",

  columnName: "Type",
  columnCode: "Code",
  columnGroup: "Category",
  columnDays: "Days",
  columnStatus: "Status",
  columnActions: "Actions",
  daysUnset: "—",

  statusAll: "All",
  statusActive: "Active",
  statusInactive: "Inactive",
  searchPlaceholder: "Search by name or code",
  filterStatus: "Status",

  deactivateTitle: "Deactivate leave type",
  deactivateBody:
    "The type will no longer appear when filing new requests. Past requests and employee grants remain unchanged.",
  deactivateConfirm: "Deactivate",

  emptyTitle: "No leave types yet",
  emptyLead: "Add the first leave type to make it available on employee records and in requests.",
  emptyFilteredTitle: "No matching results",
  emptyFilteredLead: "Try adjusting the search or removing filters.",
  clearFilters: "Clear filters",
} as const;

/**
 * Security incident reports queue and type catalogue (Settings tab).
 */
export const incidents = {
  title: "Security reports",
  lead: "Guard-submitted incident reports from the app — status, site, and attachments.",
  requestTitle: "Report details",
  backToQueue: "Back to list",

  tabNew: "New",
  tabUnderReview: "Under review",
  tabResolved: "Resolved",
  tabRejected: "Rejected",
  tabAll: "All",
  searchPlaceholder: "Search by guard or description",

  columnGuard: "Guard",
  columnType: "Report type",
  columnArea: "Site",
  columnStatus: "Status",
  columnSubmitted: "Submitted",
  columnAttachments: "Attachments",

  statusNew: "New",
  statusUnderReview: "Under review",
  statusResolved: "Resolved",
  statusRejected: "Rejected",

  filterType: "Report type",
  filterGuard: "Guard",
  filterZone: "Site",
  filterFrom: "From date",
  filterTo: "To date",
  dateRangeHint: "Filters by the report submission date.",
  dateRangeApplied: "Applied period:",

  description: "Description",
  otherLabel: "Other details",
  location: "Submission location",
  noLocation: "No coordinates were recorded with this report.",
  attachments: "Attachments",
  noAttachments: "No attachments.",
  adminNotes: "Admin notes",
  adminNotesHint: "Shown to the guard when the report status is updated.",
  updateStatus: "Update status",
  updating: "Updating…",
  statusUpdated: "Report status updated.",
  reviewedBy: "Reviewed by",
  reviewedAt: "Reviewed at",
  supervisor: "Supervisor",

  emptyTitle: "No reports",
  emptyLead: "Reports appear here when a guard submits one from the app.",
  emptyFilteredTitle: "No matching results",
  emptyFilteredLead: "Try adjusting the search or removing filters.",
  clearFilters: "Clear filters",
} as const;

export const incidentTypes = {
  title: "Security report types",
  lead: "The list the guard picks from in the app. Deactivate a type instead of deleting it.",
  newTitle: "Add report type",
  newLead: "Define the code and names. Active types appear in the app picker.",
  editTitle: "Edit report type",
  create: "Add type",
  createCta: "Add type",
  save: "Save changes",
  saving: "Saving…",
  cancel: "Cancel",
  edit: "Edit",

  nameAr: "Arabic name",
  nameEn: "English name",
  nameEnHint: "Optional.",
  code: "Code",
  codeHint: "Lowercase letters, digits, and underscores. Immutable after create.",
  codeImmutable: "The code stays fixed after create so apps and integrations can rely on it.",
  color: "Colour",
  colorHint: "Optional. Format #RRGGBB.",
  sortOrder: "Display order",
  sensitive: "Sensitive (stronger supervisor alert)",
  sensitiveHint: "When a guard submits this type, the supervisor gets a clearer alert.",
  allowsOtherText: "Allows free text (e.g. Other)",
  allowsOtherTextHint: "When enabled, the guard must enter a short extra label with the type.",
  active: "Active",

  columnName: "Type",
  columnCode: "Code",
  columnSensitive: "Sensitive",
  columnOther: "Free text",
  columnStatus: "Status",
  columnActions: "Actions",

  statusAll: "All",
  statusActive: "Active",
  statusInactive: "Inactive",
  searchPlaceholder: "Search by name or code",
  filterStatus: "Status",

  deactivateTitle: "Deactivate report type",
  deactivateBody: "The type will no longer appear in the app picker. Past reports remain unchanged.",
  deactivateConfirm: "Deactivate",

  emptyTitle: "No report types yet",
  emptyLead: "Add the first type to make it available in the app.",
  emptyFilteredTitle: "No matching results",
  emptyFilteredLead: "Try adjusting the search or removing filters.",
} as const;

/**
 * Queue for mobile single-device rebinds. Admin only — approving swaps the
 * bound handset id on the account.
 */
export const deviceChanges = {
  title: "Phone or device change",
  lead: "Requests from people who no longer have their registered number or who switched phones. Verify the requester's identity before approving; the number receives account recovery codes, and attendance is accepted only from the bound device.",

  tabPhone: "Mobile number",
  tabDevice: "Device",
  tabPending: "Under review",
  tabApproved: "Approved",
  tabRejected: "Rejected",
  tabAll: "All",
  searchPlaceholder: "Search by name or employee number…",

  columnRequester: "Requester",
  columnNumber: "Number",
  columnAction: "Action",
  idSuffix: "ID ends with",
  currentNumber: "Current",
  newNumber: "New",
  decidedBy: "Decided by",
  approveNumber: "Approve number",
  supervisorNote: "Supervisor — operations reviews this request",
  columnRole: "Role",
  columnPhone: "Phone",
  columnPreviousDevice: "Current device",
  columnNewDevice: "New device",
  columnFiled: "Date filed",
  columnStatus: "Status",
  columnReason: "Reason",

  noPreviousDevice: "—",
  noPhone: "—",
  noReason: "No reason given.",

  requestTitle: "Device change request",
  backToQueue: "Back to queue",

  timelineTitle: "Request history",
  timelineFiled: "Request filed",
  timelineDecided: "Decision made",
  timelineBy: "by",

  approve: "Approve",
  reject: "Reject",
  approveTitle: "Approve the new number",
  approveBody:
    "Have you verified the requester's identity? The new number will receive account recovery codes, and the previous number will not. The decision is sent by text message to the new number.",
  approveConfirm: "Approve number",
  rejectTitle: "Reject",
  rejectReasonLabel: "Rejection reason",
  rejectReasonHint: "Sent by text message to the new number.",
  rejectConfirm: "Reject",

  approved: "Device change approved.",
  rejected: "Device change request rejected.",

  emptyTitle: "No requests",
  emptyLead: "Requests appear here when filed from the mobile app.",
  emptyFilteredTitle: "No matching results",
  emptyFilteredLead: "Try adjusting the search or removing filters.",
  clearFilters: "Clear filters",
  readOnlyNote: "You have view-only access to these requests.",
} as const;

/** The Attendance module: the punch log and its corrections. */
export const attendance = {
  title: "Attendance",
  lead: "Check-in and check-out log, and manual corrections when needed.",

  columnGuard: "Guard",
  columnType: "Type",
  columnTime: "Time",
  columnZone: "Zone",
  columnStatus: "Geofence status",
  columnSource: "Source",
  columnLate: "Late",
  columnAccuracy: "Location accuracy",

  checkIn: "Check-in",
  checkOut: "Check-out",
  sourceMobile: "App",
  sourceManual: "Manual correction",
  sourceKiosk: "Site device",
  insideZone: "Inside zone",
  outsideZone: "Outside zone",
  noPosition: "No location",
  noZone: "No zone",
  onTime: "On time",
  unscheduled: "No scheduled shift",

  searchPlaceholder: "Search by guard name",
  filterType: "Type",
  filterStatus: "Geofence status",
  filterSource: "Source",
  filterGuard: "Guard",
  filterZone: "Zone",
  filterFrom: "From date",
  filterTo: "To date",
  dateRangeApplied: "Applied period:",

  /* The exceptions queue. The prototype read "on duty" from the last attendance
     row ever recorded, with no date bound, and dropped the unclosed punch from
     duty hours — so this state was both invisible and silently wrong. */
  unclosedTitle: "Unclosed check-ins",
  unclosedLead: "Check-in with no matching check-out. Shown here 12 hours after the punch.",
  unclosedSince: "Open since",
  hours: "hours",

  /* Manual correction */
  correct: "Manual correction",
  correctTitle: "Manual punch",
  correctLead: "For a check-in or check-out not recorded from the app. Recorded under your name with a reason.",
  correctGuard: "Guard",
  correctType: "Type",
  correctAt: "Time",
  correctAtHint: "Riyadh time.",
  correctReason: "Correction reason",
  correctReasonHint: "Required. Shown in the attendance log and audit trail.",
  correctSubmit: "Record",
  corrected: "Correction recorded.",
  correctedBy: "Corrected by",
  closeUnclosed: "Record check-out",

  emptyTitle: "No records",
  emptyLead: "Attendance records arrive from the guard app.",
  emptyFilteredTitle: "No matching results",
  emptyFilteredLead: "Try adjusting the period or removing filters.",
  clearFilters: "Clear filters",
  readOnlyNote: "You have view-only access to the attendance log.",

  errors: {
    guardRequired: "Select a guard.",
    typeInvalid: "Select a punch type.",
    timeInvalid: "Time is invalid.",
    reasonRequired: "Enter a correction reason.",
    reasonTooLong: "Reason is too long.",
  },
} as const;

/** Live tracking. */
export const tracking = {
  title: "Live tracking",
  lead: "Guard locations on the plan, with connection status and geofence violations.",

  /* Three states, not two. "No signal" is the guard the prototype passed to the
     map as {lat: null, lng: null}. */
  onDuty: "On duty",
  offDuty: "Off duty",
  noSignal: "No signal",
  rosterTitle: "Guards",
  rosterEmpty: "No active guards.",
  neverPinged: "Never sent a location",
  lastSeen: "Last signal",
  stale: "Signal stale",
  outsideZone: "Outside their zones",
  onBreak: "On break",
  workedTime: "Worked time",
  accuracy: "Accuracy",
  focus: "Focus on plan",
  clearFocus: "Clear focus",

  /* The live-update control. Clicking pauses or resumes the stream + fallback
     poll; the label reflects the *operator's* choice, not only the socket. */
  connected: "Live updates active",
  connecting: "Connecting…",
  disconnected: "Live updates stopped",
  liveToggle: "Turn live updates on or off",
  refresh: "Refresh now",
  updatedAt: "Last updated",
  liveHint: "Click the status to pause or resume. Locations refresh every 30 seconds and on each new signal.",

  toastBreakExceeded: "A guard exceeded their allowed break",
  toastAccountSuspended: "A guard account was suspended for break overrun",
  toastOutOfZone: "A guard is outside the geofence without leave",

  /* Trail playback: the read the locations table never had. */
  trailTitle: "Guard trail",
  trailPoints: "Point count",
  trailEmpty: "No points recorded in this period.",
  showTrail: "Show trail",
  hideTrail: "Hide trail",

} as const;

/** Reports. */
export const reports = {
  title: "Reports",
  lead: "Worked hours and punctuality, calculated in Riyadh time against the published rota.",

  /* Range */
  rangeFrom: "From date",
  rangeTo: "To date",
  apply: "Apply",
  presetToday: "Today",
  preset7: "7 days",
  preset30: "30 days",
  presetThisMonth: "This month",
  presetLastMonth: "Last month",
  rangeApplied: "Period:",
  /* The prototype accepted from > to and returned an empty result that looked
     exactly like a quiet period. */
  rangeSwapped: "Date order was corrected.",

  filterGuard: "Guard",
  filterZone: "Zone",
  filterSupervisor: "Supervisor",

  /* KPI row */
  kpiPunctuality: "Punctuality",
  kpiCoverage: "Hours coverage",
  kpiWorked: "Hours worked",
  kpiScheduled: "Hours scheduled",
  kpiLate: "Late minutes",
  kpiAbsences: "Absences",
  kpiOutside: "Geofence violations",
  kpiUnclosed: "Unclosed punches",
  noData: "No data in this period",

  /* Charts */
  trendTitle: "Punctuality over time",
  trendLead: "On-time check-in rate per day. Days with no scheduled shifts appear blank, not zero.",
  trendAxisRate: "Rate",
  hoursByZoneTitle: "Scheduled hours by zone",
  leaveMixTitle: "Approved leave breakdown",
  leaveMixDays: "days",
  leaveMixType: "Type",
  punctualityByZoneTitle: "Punctuality by zone",
  punctualityByZoneLead:
    "The least punctual zones are highlighted; the rest fade — the question is which site is the problem, not a league table of everyone against everyone.",
  scheduledPunches: "Scheduled punches",
  coverageTitle: "Schedule coverage",
  coverageLead: "Hours actually worked versus hours the schedule required in this period.",
  coverageMeterLabel: "Coverage rate",
  ofScheduled: "of",
  heatmapTitle: "Punch distribution — days and hours",
  heatmapLead:
    "When work actually happens: check-in and check-out count per hour of each weekday, in site time. Darker cells are busier; blank cells had no punches at all.",
  chartEmpty: "No data to chart in this period.",
  chartTarget: "Target",
  ofTotal: "of",

  /* Table */
  columnGuard: "Guard",
  columnSupervisor: "Supervisor",
  columnScheduled: "Scheduled",
  columnWorked: "Worked",
  columnLate: "Late",
  columnEarly: "Early leave",
  columnPunctuality: "Punctuality",
  columnAbsences: "Absences",
  columnLeave: "Leave",
  columnOutside: "Outside zone",
  notScheduled: "Not scheduled",

  /* Export and print */
  exportCsv: "Export CSV",
  print: "Print",
  /* Excel needs a byte-order mark to read UTF-8 Arabic; without it the file
     opens as mojibake. Worth stating, because it is the commonest defect in an
     Arabic CSV export. */
  exportNote: "File is UTF-8 encoded with Western numerals for Excel and spreadsheets.",
  printedAt: "Printed at",

  emptyTitle: "No data in this period",
  emptyLead: "Try a wider period, or confirm the shift rota is published — reports are measured against the published rota.",
} as const;

/** Organisation settings, and the operator's own account. */
export const settings = {
  title: "Settings",
  lead: "Organisation settings and your personal account.",

  tabOrg: "Organisation",
  tabProfile: "My account",
  tabSecurity: "Security",
  tabReportTypes: "Report types",

  /* Organisation — every one of these was a literal in the prototype's source. */
  orgTitle: "Organisation settings",
  orgLead: "Apply to all users and are used for calculations in reports.",
  timeZone: "Time zone",
  timeZoneHint: "All calculations — shifts, lateness, and reports — use this zone.",
  mapCentre: "Default map centre",
  mapCentreHint: "Where the map opens when no boundary has been drawn yet.",
  mapLat: "Latitude",
  mapLng: "Longitude",
  defaultPolicy: "Default geofence policy",
  defaultPolicyHint: "Applied to a new zone, and to a guard not assigned to any zone.",
  staleMinutes: "Minutes until signal is considered stale",
  staleMinutesHint: "After this, the guard appears with no signal on the tracking board.",
  retentionDays: "Days to retain guard location history",
  retentionDaysHint: "Trail points older than this are deleted. Attendance records are not deleted.",
  inviteExpiry: "Activation link validity (hours)",
  minPassword: "Minimum password length",
  maxActiveEmployees: "Maximum active accounts",
  maxActiveEmployeesHint:
    "Used for companies that have not saved the three limits yet, and for people with no company. Leave blank for no limit.",
  maxSupervisors: "Default supervisor limit",
  maxSupervisorsHint: "Copied onto a new company when its supervisor field is left blank. Leave blank for no limit.",
  maxGuards: "Default guard limit",
  maxGuardsHint: "Copied onto a new company when its guard field is left blank. Leave blank for no limit.",
  maxAreas: "Default area limit",
  maxAreasHint: "Copied onto a new company when its area field is left blank. Leave blank for no limit.",
  notifyBreach: "Notify on geofence breach",
  notifyBreachHint: "Sent to the supervisor responsible for the guard.",
  saveOrg: "Save settings",
  orgSaved: "Settings saved.",

  /* Profile */
  profileTitle: "My account",
  name: "Name",
  phone: "Mobile number",
  email: "Email",
  emailReadOnly: "Email cannot be changed here. Contact your system administrator.",
  language: "Interface language",
  languageArabic: "العربية",
  languageEnglish: "English",
  languageHint: "Applies to menus, forms, and system messages in this session.",
  numerals: "Displayed numeral system",
  numeralsArabic: "Arabic numerals (٠١٢٣)",
  numeralsWestern: "Western numerals (0123)",
  numeralsHint: "Applies to tables, reports, and charts. Exports always use Western numerals.",
  saveProfile: "Save",
  profileSaved: "Your details were saved.",

  /* Security */
  securityTitle: "Security",
  changePassword: "Change password",
  currentPassword: "Current password",
  newPassword: "New password",
  confirmPassword: "Confirm password",
  passwordSaved: "Password changed.",
  sessionsTitle: "Active sessions",
  sessionsLead: "Every device signed in to your account. Includes the guard app if you use it.",
  sessionCurrent: "This session",
  sessionLastSeen: "Last activity",
  sessionSignOut: "End",
  signOutOthers: "End other sessions",
  signOutOthersTitle: "End other sessions",
  signOutOthersBody: "All sessions except this one will end. You will need to sign in again on other devices.",
  signOutOthersConfirm: "End",
  sessionsRevoked: "Other sessions ended.",
  sessionRevoked: "Session ended.",
  sessionsEmpty: "No other sessions.",

  readOnlyNote: "Organisation-wide settings require a system administrator. You can edit your account and security settings.",

  errors: {
    generic: "Could not complete the operation. Try again.",
  },
} as const;

export const notificationText = {
  "leave_request:new": "New leave request",
  "leave_request:decided": "Your leave request was decided",
  "leave_request:reopened": "Leave request reopened",
  "geofence:breach": "Check-in outside geofence",
  "shift:coverage_gap": "Shift coverage gap",
  "shift:published": "Shift rota published",
  "attendance:unclosed": "Unclosed check-in",
  "attendance:break_exceeded": "Break time exceeded",
  "attendance:account_suspended": "Account suspended for break overrun",
  "account:invited": "Your account was created",
  "hr:expiry": "HR document or contract expiry",
  "company:contract_expiry": "Company contract nearing expiry",
  "device_change:decided": "Your login device change request was decided",
  "break_request:new": "New break request",
  "break_request:decided": "Your break request was decided",
} as const;

export const notifications = {
  title: "Notifications",
  lead: "Full history, not unread only.",

  tabAll: "All",
  tabUnread: "Unread",
  /* The toggle is labelled by what clicking gives, not by the current state. */
  sortOldestFirst: "Oldest first",
  sortNewestFirst: "Newest first",
  markAllRead: "Mark all as read",
  markRead: "Mark as read",
  allRead: "All marked as read.",
  unreadBadge: "New",
  open: "Open",
  /* Its notifications were dead ends: clicking one marked it read and removed it
     from the list, and the `referenceId` it serialised for exactly this purpose
     was used by nothing. */
  emptyTitle: "No notifications",
  emptyLead: "Notifications appear here when a leave request arrives, a rota is published, or a geofence is breached.",
  emptyUnreadTitle: "No unread notifications",
  emptyUnreadLead: "All notifications are read.",
} as const;

/** The audit log. */
export const audit = {
  title: "Audit log",
  lead: "Who changed what and when, and from which address.",

  columnWhen: "Time",
  columnActor: "Actor",
  columnAction: "Action",
  columnEntity: "Entity",
  columnChange: "Change",
  columnIp: "IP address",

  filterActor: "Actor",
  filterAction: "Action",
  filterEntity: "Entity",
  searchPlaceholder: "Search entity",

  actionCreate: "Create",
  actionUpdate: "Update",
  actionDelete: "Delete or archive",
  actionRestore: "Reactivate",

  system: "System",
  before: "Before",
  after: "After",
  noChange: "No details recorded.",
  showChange: "Show change",

  emptyTitle: "No changes recorded",
  emptyLead: "Every write operation on the platform is logged here.",
  emptyFilteredTitle: "No matching results",
  emptyFilteredLead: "Try removing some filters.",
  clearFilters: "Clear filters",
} as const;

/** The display-theme choice, in the top bar. */
export const theme = {
  label: "Theme",
  paper: "Light",
  navy: "Dark",
} as const;

/** Table paging controls. */
export const pagination = {
  page: "Page",
  of: "of",
  none: "No results",
  previous: "Previous page",
  next: "Next page",
  first: "First page",
  last: "Last page",
  perPage: "Rows",
} as const;

/** The shared filter bar. */
export const filters = {
  all: "All",
  clear: "Clear filters",
  apply: "Apply",
  updating: "Updating",
} as const;

/**
 * The command palette.
 *
 * Opened with Ctrl+K, or Cmd+K on a Mac. The hint names both, because the console
 * is used on Windows workstations and on a manager's laptop and guessing wrong
 * makes the affordance invisible to half the users.
 */
export const palette = {
  open: "Search and navigate",
  shortcutHint: "Ctrl K",
  placeholder: "Search for an employee or zone, or go to a screen…",
  sectionPages: "Screens",
  sectionEmployees: "Employees",
  sectionZones: "Zones",
  searching: "Searching…",
  /* Said rather than left blank: an empty panel reads as broken, and the reason
     is a real rule — two letters, because one letter matches everything. */
  typeMore: "Type at least two characters to search employees and zones.",
  noResults: "No matching results.",
  inactiveTag: "Inactive",
  hintNavigate: "to navigate",
  hintSelect: "to open",
  hintClose: "to close",
} as const;

/**
 * The 404.
 *
 * The prototype redirected any unknown URL to `/`, which bounced again by role —
 * so a mistyped address gave no feedback and looked exactly like being signed
 * out. Its own group because the page renders on both sides of the session
 * boundary and belongs to neither.
 */
export const notFound = {
  title: "Page not found",
  lead: "The link you followed is wrong or out of date. If you arrived from inside the console, let us know.",
  backToConsole: "Back to console",
} as const;

/** Loading and failure states shared by every screen that reads. */
export const queryState = {
  errorTitle: "Could not load data",
  errorLead: "The service did not respond. Try refreshing the page; if it persists, let us know.",
  retry: "Try again",
} as const;

/** A record that does not exist, shown inside the console shell. */
export const recordNotFound = {
  title: "Record not found",
  lead: "The ID in the link has no record — it may have changed or been removed. Check the list.",
} as const;

/** The shared confirmation dialog. */
export const confirm = {
  cancel: "Cancel",
  working: "Working…",
} as const;

/**
 * Scheduled app maintenance windows.
 */
export const maintenance = {
  title: "Maintenance",
  lead: "Schedule app maintenance windows across platforms.",
  createCta: "Add maintenance",

  columnPlatforms: "Platform",
  columnStartsAt: "From",
  columnEndsAt: "To",
  columnResponsible: "Responsible",
  columnReason: "Reason",
  columnStatus: "Status",
  columnActions: "Actions",

  statusScheduled: "Scheduled",
  statusActive: "Active",
  statusEnded: "Ended",
  statusInactive: "Inactive",

  platformAndroid: "Android",
  platformIos: "iOS",
  platformAppGallery: "App Gallery",
  platformWeb: "Web",

  emptyTitle: "No maintenance windows",
  emptyLead: "Add a maintenance window to notify apps when service is down.",

  formCreateTitle: "Add maintenance",
  formEditTitle: "Edit maintenance",
  formViewTitle: "View maintenance",
  startsAt: "Maintenance starts",
  endsAt: "Maintenance ends",
  platforms: "Platforms",
  platformsHint: "This window applies to every selected platform.",
  minimumSupportedVersion: "Minimum supported version",
  currentVersion: "Current version",
  reasonAr: "Maintenance reason (Arabic)",
  reasonEn: "Maintenance reason (English)",
  messageAr: "Maintenance message (Arabic)",
  messageEn: "Maintenance message (English)",
  isActive: "Active",
  isActiveHint: "Inactive windows are hidden from apps even during their schedule.",
  responsible: "Responsible",
  responsibleHint: "Recorded automatically as the creator.",
  save: "Save",
  saving: "Saving…",
  cancel: "Cancel",
  edit: "Edit",
  view: "View",
  delete: "Delete",
  deleteTitle: "Delete this maintenance window?",
  deleteBody: "The record is removed permanently and will no longer appear to apps.",
  deleteConfirm: "Delete",
  deactivateTitle: "Deactivate this maintenance window?",
  deactivateBody: "It will not appear to apps until it is activated again.",
  deactivateConfirm: "Deactivate",
  backToList: "Back to maintenance",
} as const;

/**
 * App version / store update records (one platform per row).
 */
export const updates = {
  title: "Updates",
  lead: "Publish app version releases per platform.",
  createCta: "Add update",

  columnPlatform: "Platform",
  columnUpdateType: "Update type",
  columnCurrentVersion: "Current version",
  columnResponsible: "Responsible",
  columnReason: "Update reason",
  columnUpdateDate: "Update date",
  columnActions: "Actions",

  typeOptional: "Optional",
  typeForced: "Forced",

  platformAndroid: "Android",
  platformIos: "iOS",
  platformAppGallery: "App Gallery",
  platformWeb: "Web",

  emptyTitle: "No update records",
  emptyLead: "Add an update record so apps can check for a new version.",

  formCreateTitle: "Add update",
  formEditTitle: "Edit update",
  formViewTitle: "View update",
  platform: "Platform",
  platformHint: "One platform per record — create separate rows for Android, iOS, and others.",
  updateType: "Update type",
  minimumSupportedVersion: "Minimum supported version",
  currentVersion: "Current version",
  reasonAr: "Update reason (Arabic)",
  reasonEn: "Update reason (English)",
  responsible: "Responsible",
  responsibleHint: "Recorded automatically as the creator.",
  save: "Save",
  saving: "Saving…",
  cancel: "Cancel",
  edit: "Edit",
  view: "View",
  delete: "Delete",
  deleteTitle: "Delete this update record?",
  deleteBody: "The record is removed permanently and will no longer appear to apps.",
  deleteConfirm: "Delete",
  backToList: "Back to updates",
} as const;

/**
 * Bilingual policy pages shown in the app and on the public policy route.
 */
export const policies = {
  title: "Policies",
  lead: "Saved content here is displayed in the app and website in the user's selected language.",
  add: "Add policy",
  published: "Published",
  hidden: "Hidden",
  publishedHint: "Appears in the app and website.",
  arabic: "Arabic",
  english: "English",
  unsaved: "Unsaved changes",
  save: "Save",
  saving: "Saving…",
  edit: "Edit",
  delete: "Delete",
  preview: "Preview",
  deleteTitle: "Delete policy?",
  deleteBody:
    "«{name}» and its Arabic and English content will be deleted. This cannot be undone.",
  deleteConfirm: "Delete",
  cancel: "Cancel",
  addTitle: "Add policy",
  addLead: "The policy name is shown according to the selected application language.",
  nameAr: "Policy name (Arabic)",
  nameEn: "Policy name (English)",
  nameArPlaceholder: "مثال: سياسة الشحن",
  nameEnPlaceholder: "e.g. Shipping Policy",
  permanentLink: "Permanent link",
  slugDerivedHint: "Derived from the English name, and stays fixed after creation.",
  slugFixedHint: "Does not change when the name is edited, so shared links keep working.",
  editTitle: "Edit policy details",
  saveChanges: "Save changes",
  unsavedTitle: "Unsaved changes",
  unsavedBody:
    "You have unsaved changes in «{name}». If you continue, those changes will be lost.",
  discard: "Discard changes",
  saveAndContinue: "Save and continue",
  emptyTitle: "No policies",
  emptyLead: "Add a policy for people to read in the app.",
  lastUpdate: "Last update: {date}",
  notFoundTitle: "Policy unavailable",
  notFoundLead: "This link does not match a published policy.",

  errors: {
    titleRequired: "Policy name is required.",
    titleTooLong: "Policy name is too long.",
    contentTooLong: "Content is too long. Paste plain text or remove heavy formatting.",
  },
} as const;

/**
 * The client companies — the counterparties Teslm guards sites for.
 *
 * Wording note: "client company" throughout, never just "company". The operator's own
 * employer is also a company, and an admin reading "deactivate the company" on a
 * destructive-looking control should be in no doubt which one is meant.
 */
export const companies = {
  title: "Client companies",
  lead: "The organisations whose sites we guard, and who on our team follows them.",
  newTitle: "Add a client company",
  newLead: "The company is created with one login, and an activation link is sent to it.",
  editTitle: "Edit company details",
  create: "Add company",
  save: "Save changes",
  saving: "Saving…",
  cancel: "Cancel",

  /* Fields */
  nameAr: "Name in Arabic",
  nameEn: "Name in English",
  nameEnHint: "Optional. Not shown in the console yet.",
  commercialRegistration: "Commercial registration",
  commercialRegistrationHint: "10 digits. Dashes and spaces are removed automatically.",
  vatNumber: "VAT number",
  vatNumberHint: "Optional — if applicable. 15 digits.",
  bankAccount: "Bank account (IBAN)",
  bankAccountHint: "Optional. Converted to uppercase without spaces.",
  establishmentNumber: "Establishment unified number",
  establishmentNumberHint: "Optional. Required to export the wage-protection file.",
  limitsHeading: "Company limits",
  limitsIntroCreate:
    "The maximum number of active supervisors, active guards, and areas. A blank field takes the general limit from system settings.",
  limitsIntroEdit:
    "A change applies as soon as you save. A blank field means no limit. Lowering a limit below current usage does not deactivate anyone; new additions stay blocked until usage drops.",
  limitBelowUsage: "Below current usage ({used}). Nobody is deactivated, and new additions stay blocked until usage drops.",
  limitSupervisors: "Supervisors",
  limitGuards: "Guards",
  limitAreas: "Areas",
  maxSupervisors: "Maximum supervisors",
  maxGuards: "Maximum guards",
  maxAreas: "Maximum areas",
  maxLimitHintCreate: "Leave blank to use the organisation default from system settings.",
  maxLimitHintEdit:
    "Leave blank for no limit. Lowering a limit below current usage does not deactivate anyone; new additions are blocked until usage drops.",
  notes: "Notes",
  notesHint: "Optional. Not shown to the company.",
  email: "Login email",
  emailHint: "Receives the password setup message and is used by the company to access its data.",
  emailImmutable: "Login email cannot be changed here. Add another company account if needed.",

  /* Columns */
  columnName: "Company",
  columnRegistration: "Commercial registration",
  columnTeam: "Team",
  columnSupervisors: "Supervisors",
  columnGuards: "Guards",
  columnAreas: "Areas",
  columnAccount: "Client account",
  columnZones: "Sites",
  columnStatus: "Status",
  columnCreated: "Date added",
  columnPerson: "Name",
  columnSupervisor: "Supervisor",
  columnPhone: "Mobile",

  /* States */
  /* Every client still in service — active and suspended together, excluding archived. It is
     the default view, so the row switch never makes a row leave the screen. */
  statusAll: "All",
  statusActive: "Active",
  statusSuspended: "Suspended",
  statusArchived: "Archived",
  notActivated: "Not activated yet",
  noLogin: "No login account",
  neverSignedIn: "Never signed in",
  /* Counts */
  supervisorsCount: "supervisors",
  guardsCount: "guards",
  zonesCount: "sites",

  /* Filters and search */
  searchPlaceholder: "Search by name or commercial registration…",
  filterState: "Status",
  filterLimits: "Limits",
  filterContracts: "Contracts",
  limitsNear: "Near the limit",
  limitsFull: "At or over the limit",
  contractsEnding: "A contract ends within 70 days",
  actionsLabel: "Actions",

  /* Actions */
  view: "View",
  edit: "Edit company data",
  backToCompanies: "Back to companies",
  backToCompany: "Back to the company",
  suspend: "Suspend",
  reactivate: "Reactivate",
  archive: "Archive",
  restore: "Restore",
  resendInvite: "Resend activation link",
  resetPassword: "Send password reset link",

  /* Confirmations. Each one says what happens to the client's access, because
     that is the part an admin is deciding and the part easiest to overlook. */
  suspendTitle: "Suspend company",
  suspendBody:
    "The company will not be able to sign in, and its current sessions will end. Its supervisors and guards continue working normally, all data remains unchanged, and you can reactivate it at any time.",
  suspendConfirm: "Suspend",
  /* Said before the switch is flipped, not discovered after. The list defaults
     to the live clients, so a suspended one leaves the view — which looks like a
     deletion if nobody warned you. */
  suspendLeavesList: "The company will disappear from the active companies list. Find it under the «Suspended» filter.",
  reactivateTitle: "Reactivate company",
  reactivateBody: "The company will be able to sign in again with the same password.",
  reactivateConfirm: "Reactivate",
  archiveTitle: "Archive company",
  archiveBody:
    "The company is removed from active lists and sign-in stops. Its commercial registration number and name become available for another company. Historical data is retained.",
  archiveConfirm: "Archive",
  restoreTitle: "Restore company",
  restoreBody: "The company returns to active lists. Sign-in remains suspended until you reactivate it.",
  restoreConfirm: "Restore",
  resetPasswordTitle: "Send password reset link",
  resetPasswordBody:
    "The company's email will receive a link to set a new password. The current password remains valid until the new one is set, and any link sent earlier will stop working. When set, sessions on all devices will end.",
  resetPasswordConfirm: "Send link",

  /* Outcomes */
  created: "Company added and activation link sent to its email.",
  updated: "Changes saved.",

  /* Detail page */
  profileHeading: "Company details",
  accessHeading: "Login accounts",
  accessLead: "Who opens the client portal and sees its guards and sites.",
  addLogin: "Add a login account",
  addLoginLead: "This address receives a link to set a password. The invite stays pending until it is used.",
  loginName: "Client contact name",
  loginEmail: "Email",
  sendInvite: "Send invite",
  pendingInvite: "Pending invitation",
  addedOn: "Date added",
  emptyValue: "—",
  noLimit: "No limit",
  viewTeam: "View team",
  usageUsed: "used",
  usageRemaining: "remaining",
  usageLimit: "limit",
  usageOver: "over by",
  usageUnlimited: "used {used} · no limit",
  usageUnderLine: "used {used} · remaining {remaining} (limit {limit})",
  usageAtLine: "used {used} · remaining {remaining} · limit reached",
  usageOverLine: "used {used} · over by {over} (limit {limit})",
  nearLimit: "Near the limit",
  atLimit: "Limit reached",
  overLimit: "Over the limit",
  overBanner: "The company is over its {kind} limit by {count} — additions stay blocked until usage drops, and nobody was deactivated.",
  atBanner: "The company has reached its {kind} limit — additions stay blocked until a place opens.",
  nearBanner: "The company is near its {kind} limit.",
  staffedSites: "{staffed} of {total} fully staffed",
  assignedVsContracts: "Assigned / by contract",
  mismatchShort: "Does not match the contracts",
  matchShort: "Matches the contracts",
  guardsOnPlan: "Guards on the plan",
  sitesOnPlan: "Company areas on the plan",
  planHint: "An approximate plan of the site boundaries. Drag to pan, and use the scroll wheel or the buttons to zoom.",
  siteStaffing: "{on} of {required}",
  teamHeading: "Company team",
  teamLead: "Our supervisors and guards working on this client's areas.",
  teamSearch: "Search by name, supervisor, or mobile",
  inactiveNotCounted: "{count} in this list have a suspended account — they are not in the count or the limit.",
  noGuardsTitle: "No guards on this company's areas",
  zonesHeading: "Sites",
  contractsHeading: "Sites & Contracts",
  contractsLead:
    "Client sites and contracts. A company may have more than one. Alerts fire at 70 days or fewer before expiry, using the same style of windows as employee contract alerts.",
  analyticsHeading: "Guarding performance for this client",
  analyticsLead: "Hours and punctuality on this client's sites.",
  workedOfScheduled: "Hours worked of those scheduled",
  workedOfScheduledValue: "{worked} of {scheduled}",
  createdBy: "Added by",
  lastUpdated: "Last updated",
  noSupervisorsTitle: "No supervisors yet",
  noSupervisorsLead: "Assign a supervisor to this company from the Employees screen; guards follow them automatically.",
  assignSupervisor: "Assign supervisor",
  noZonesTitle: "No sites yet",
  noZonesLead: "Add a site and assign it to this company so its operations appear here.",
  addZone: "Add site",
  guardsOfSupervisor: "their guards",
  noContractsTitle: "No contracts yet",
  noContractsLead: "Add a site or contract for this company.",
  addContract: "Add contract",
  editContract: "Edit contract",
  removeContract: "Delete contract",
  removeContractTitle: "Delete this contract?",
  removeContractBody: "This contract will be permanently removed from the company record.",
  removeContractConfirm: "Delete",
  contractClientName: "Client / company name",
  contractClientNameHint: "May match the registered company name, or a different client entity.",
  contractStartDate: "Contract start date",
  contractEndDate: "Contract end date",
  contractEndDateHint: "The team is alerted at 70 days or fewer before expiry, with the same window style as employee contracts.",
  contractValue: "Contract value",
  contractedEmployees: "Number of employees",
  contractedEmployeesHint: "Contracted headcount of guards/employees for this site.",
  contractContactName: "Authorized contact person (client-side)",
  contractContactEmail: "Contact person email",
  contractContactMobile: "Contact person mobile",
  contractZone: "Geographic site (optional)",
  contractZoneHint: "Link the contract to a company zone to compare contracted vs assigned guards.",
  contractZoneNone: "No zone link",
  contractSaved: "Contract saved.",
  contractDeleted: "Contract deleted.",
  headcountStrip: "Headcount comparison",
  headcountContracted: "Contracted",
  headcountAssigned: "Actually assigned",
  headcountMismatch: "Contracted headcount differs from assigned headcount.",
  headcountMatch: "Contracted headcount matches employees assigned to the company.",
  daysUntilExpiry: "Left",
  days: "days",
  expirySoon: "Expiring soon",
  expired: "Expired",
  columnClient: "Client",
  columnPeriod: "Period",
  columnValue: "Value",
  columnHeadcount: "Employees",
  columnContact: "Contact",
  columnExpiry: "Expiry",

  /* Analytics tiles. Same vocabulary as the reports screen, on purpose: the same
     number must not have two names. */
  rangeLabel: "Period",
  range30: "Last 30 days",
  range7: "Last 7 days",
  range90: "Last 90 days",
  workedHours: "Hours worked",
  scheduledHours: "Hours scheduled",
  punctualityRate: "Punctuality",
  lateMinutes: "Late minutes",
  absences: "Absences",
  outsideZone: "Out-of-zone punches",
  unclosedPunches: "Unclosed punches",
  noAnalytics: "No data in this period.",

  /* Empty states */
  emptyTitle: "No companies yet",
  emptyLead: "Add the first client company, then assign supervisors and guards to it.",
  emptyFilteredTitle: "No matching results",
  emptyFilteredLead: "Try adjusting the search or changing the status filter.",
  clearFilters: "Clear filters",

  /*
   * Validation messages.
   *
   * These seven only, because they are the ones the shared zod schema renders in
   * the browser before a request is ever made. Every other companies error — a
   * registration already taken, a name already used — is a uniqueness verdict
   * only the database can reach, so the API owns its wording and this catalogue
   * must not hold a second copy of it to drift.
   */
  errors: {
    nameTooShort: "Company name must be at least 2 characters.",
    nameTooLong: "Company name is too long.",
    crInvalid: "Commercial registration must be 10 digits.",
    vatInvalid: "VAT number must be 15 digits, or leave it blank.",
    ibanInvalid: "Account number is invalid. Example: SA0380000000608010167519",
    notesTooLong: "Notes are too long.",
    emailInvalid: "Email address is invalid.",
    clientNameRequired: "Client name is required.",
    clientNameTooLong: "Client name is too long.",
    contractDatesRequired: "Start and end dates are required.",
    contractDateInvalid: "Date format is invalid.",
    contractEndBeforeStart: "Contract end date must be on or after the start date.",
    contractValueRequired: "Contract value is required.",
    contractValueInvalid: "Contract value is invalid.",
    contractedCountRequired: "Contracted employee count is required.",
    contractedCountInvalid: "Employee count must be a whole number greater than zero.",
    contactNameRequired: "Contact person name is required.",
    contactEmailInvalid: "Contact person email is invalid.",
    contactMobileInvalid: "Contact person mobile number is invalid.",
    contractsJsonInvalid: "Contracts list is invalid.",
  },
} as const;

/**
 * Weekday names.
 *
 * @dynamic — every read is a computed lookup (`weekdays[key]`) from the
 * heatmap's row axis, which the key checker cannot see. Its own export rather
 * than a nested object inside `reports`, because marking the whole reports group
 * dynamic would switch off the unused-key check for fifty real strings.
 */
export const weekdays = {
  sunday: "Sunday",
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
} as const;

/** The shared KPI tile: the comparison line under a figure. */
export const metrics = {
  /* Direction is carried by an icon AND this word, never by colour alone —
     --ok against --viz-5 is ΔE 4.1 apart under protanopia. */
  up: "Up",
  down: "Down",
  flat: "No change",
  vsPrevious: "vs previous period",
  vsYesterday: "vs yesterday",
  /* A rate that moves from 80% to 40% has fallen forty POINTS, not forty
     percent — the relative drop is fifty. Labelling the difference between two
     percentages as a percentage is the same species of quiet wrongness the
     reports module exists to have removed. */
  points: "percentage points",
} as const;

/** The frame every chart sits in. */
export const charts = {
  showTable: "Show data as table",
  hideTable: "Hide table",
  tableCaption: "Same data as the chart, as a table",
  empty: "No data in this period.",
  /* Composed with fmt at the call site so the numerals follow the reader's
     setting rather than being baked into the string. */
  coverageOf: "days with data of",
  /* Named so a legend entry and a table row cannot disagree about the unit. */
  unitPunches: "Punch count",
  unitPercent: "Percent",
  unitHours: "Hours",
  unitDays: "Days",
  /* The disclosure table's own heads, shared by every chart that shows a
     per-day series. */
  tableDay: "Day",
  tableRate: "Rate",
  tableHour: "Hour",
  tableMetric: "Metric",
} as const;

export const payrollRuns = {
  eyebrow: "Payroll and attendance",
  title: "Payroll runs",
  lead: "One run per month: it recalculates while it is a draft, locks on approval, then payment is recorded.",
  create: "Create run",
  rules: "Payroll rules",
  itemTypes: "Deduction and bonus types",
  month: "Month",
  status: "Status",
  employees: "Employees",
  gross: "Gross",
  deductions: "Deductions",
  net: "Net",
  review: "Needs review",
  draft: "Draft",
  partial: "Partially approved",
  approved: "Approved",
  paid: "Paid",
  today: "Today",
  yesterday: "Yesterday",
  daysAgo: "{n} days ago",
  back: "Back to runs",
  print: "Print",
  approveRun: "Approve run",
  approveSelected: "Approve selected",
  payAll: "Record payment for all",
  paySelected: "Record payment for selected",
  exportFile: "Bank transfer file",
  banner: "Attendance records not approved: {n}",
  approveReady: "Approve the remaining records",
  showReady: "Show ready records",
  approvedOf: "Approved",
  employee: "Employee",
  overtime: "Overtime",
  bonuses: "Bonuses",
  payment: "Payment",
  bank: "Bank transfer",
  cash: "Cash",
  cheque: "Cheque",
  noIban: "No IBAN",
  paidOn: "Paid",
  all: "Show all",
  search: "Search by name or employee number",
  company: "Company",
  empty: "No employees in this view.",
  noRuns: "No runs yet.",
  issueAttendance: "Attendance record not approved",
  issueOvertime: "Pending overtime",
  issuePayment: "No IBAN or payment method",
  issueOpenShift: "Days without checkout",
  issueTask: "Task pay — enter manually",
  issueEos: "Left the job — end-of-service is outside the system",
  issuePending: "Pending deductions or bonuses",
  issueContract: "No contract",
  issueExpired: "Contract has ended",
  issueCap: "A deduction over the cap was still applied",
  issueNegative: "Negative net",
  openRecord: "Open record",
  overtimeDecision: "Overtime decision",
  addIban: "Add IBAN",
  openStatement: "Open statement",
  openFile: "Open file",
  openItems: "View items",
  addContract: "Add contract",
  openContract: "Open contract",
  approveTitle: "Approve the payroll run",
  approveBody: "The month's attendance is locked and the statements are published to the employees. Later changes are settled as a difference on the next month.",
  approveConfirm: "Approve run",
  payTitle: "Record salary payment",
  payBody: "Payment is recorded for everyone with a payment method. Anyone without an IBAN or a method stays pending.",
  payConfirm: "Record payment for all",
  cancel: "Cancel",
  skipped: "Not approved",
  createTitle: "Create a month run",
  createBody: "Includes every employee with a contract in the month, calculated from attendance, deductions, and bonuses.",
  createMonth: "Month",
  optional: "(optional)",
  submitCreate: "Create",
  reopen: "Reopen",
  reopenTitle: "Reopen the statement",
  reopenBody: "The statement returns to draft and is recalculated. Write the reason; it is kept in the change log.",
  reopenReason: "Reason",
  reopenSubmit: "Reopen",
  overtimeTitle: "Overtime decision",
  overtimeBody: "Approving the minutes keeps them in pay. Rejecting drops them from this month.",
  overtimeApprove: "Approve overtime",
  overtimeReject: "Reject overtime",
  rulesTitle: "Payroll rules",
  rulesLead: "Active rules apply automatically when a draft is calculated. An approved run keeps the rule version it was approved with.",
  ruleName: "Rule name",
  ruleFrom: "Effective from",
  ruleActive: "Active",
  ruleSave: "Save rule",
  ruleGrace: "Grace period (minutes)",
  ruleRate: "Amount per minute (SAR)",
  rulePercent: "Percent",
  ruleMultiplier: "Overtime multiplier",
  ruleHours: "Hours per day",
  ruleBasis: "Calculation basis",
  basisBasic: "Basic",
  basisWage: "Basic, housing, and transport",
  ruleCap: "Deduction cap as a share of gross %",
  itemsTitle: "Deduction and bonus types",
  itemsLead: "Names for manual items. The amount and reason stay on the employee's statement.",
  itemName: "Item name",
  itemKind: "Type",
  itemAdd: "Add type",
  itemSave: "Save",
  kindDeduction: "Deduction",
  kindBonus: "Bonus",
  kindProration: "Proration for a mid-month joiner or leaver",
  kindLateness: "Lateness",
  kindEarlyLeave: "Early leave",
  kindAbsence: "Absence",
  kindOutOfZone: "Out of zone",
  kindBreak: "Exceeded break",
  kindOvertime: "Overtime",
  kindGosi: "Social insurance",
  kindCap: "Deduction cap",
  saved: "Saved.",
  exported: "The transfer file was downloaded.",
  exportBlocked: "Some employees were left out of the file.",
  warningNet: "Warning",
} as const;
